// ╔══════════════════════════════════════════════════════════════╗
// ║  LIVETRADING.TSX — JAISWAL CAPITAL — MAMTA AI v10.0 RUDRA    ║
// ║  ✅ Bug Fix 1: BOSS_KILL_BOT moved BEFORE return in toggleDep ║
// ║  ✅ Bug Fix 2: setDeployments added in deleteBot()            ║
// ║  ✅ Bug Fix 3: Removed fake clients (Rule 2 Zero Fake Data)   ║
// ║  ✅ Upgrade 1: WebSocket real-time bridge (socket.io-client)  ║
// ║  ✅ Upgrade 2: Global Kill Switch Panel + MDD protection      ║
// ║  ✅ Upgrade 3: Expiry Rollover badge auto-detection           ║
// ║  ✅ Upgrade 4: Dashboard stats broadcast via CustomEvent      ║
// ║  ⭐ v10.0 NEW: RUDRA Dual-Weapon Panel                        ║
// ║      • Hathiyar 1: TradingView Webhook (existing)             ║
// ║      • Hathiyar 2: auto_trader_loop.py status (port 5500)     ║
// ║      • Smart Slots (20→2) live in top panel                   ║
// ║      • One-Click START/STOP + Manual Override                 ║
// ║      • Active auto-holdings visible in real-time              ║
// ║      • Memory Engine 30-day stats badge                       ║
// ║  INSTALL: npm install socket.io-client                        ║
// ╚══════════════════════════════════════════════════════════════╝
import PineHistoryPanel from './liveTrading/PineHistoryPanel';
import { usageOf, usageOfCells, inrShort, pctTxt } from './liveTrading/fundUsage';
import { laneBotView } from './components/SuperChart/chartLane';
import { subscribeLTP, getLastLTP } from './lib/marketFeed';
import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { FLEET as FLEET_REGISTRY, botOf } from './botIdentity';
import { io as socketIO, type Socket } from 'socket.io-client';
import { useManagedSymbols, managedApiSym } from './lib/stores/managedSymbols';
import { useViewport } from './hooks/useBreakpoint';
import { ExecutionArmBar } from './liveTrading/ExecutionArmBar';
import { botLabel, botSource, optLabel } from './tradeAnalytics';
import FleetRegistryCards from './components/FleetRegistryCards';
import TrafficGrid from './components/TrafficGrid';
import PromotionLadder from './components/PromotionLadder';
// 🧠 BOT MEMORY — 20-level depth book snapshots feed the bots' short memory
// (buy-low/sell-high bias input for self-analysis in MAMTA AI PRO).
import { recordDepthSnap, analyzeBot } from './services/botMemory';
// 📜 Per-symbol Pine version picker — shared component (Edit Bot Asset Basket
// + MAMTA AI PRO bot cards/editor all use the SAME isolated clean list).
import { PineVersionPicker as PineCodePicker, pineVerCache as _pineVerCache,
  pvNorm as _pvNorm, pvSymsOf as _pvSymsOf } from './components/PineVersionPicker';
// ⚡ Granular Position Manager — bot×symbol risk console (EXIT/STOP per symbol),
// RISK_LIMITS_3TIER_BLUEPRINT.md §9 UI-P1 (God directive 2026-07-15).
import { GranularPositionManager } from './components/GranularPositionManager';
import { ContestReadiness } from './components/ContestReadiness';
import { AccSection, useAccordion } from './components/ui/Smart';
import {
  Bot, Play, Square, Terminal, Activity, Plus, BarChart2, Target,
  TrendingUp, X, Webhook, Eye, Copy, List, Download, Printer, Edit,
  Zap, Cpu, Shield, Layers, Users, Briefcase, Trash2, AlertTriangle,
  Minus, Wifi, WifiOff, Calendar, RefreshCw, Bell, DollarSign, Crosshair,
  Send, ChevronDown, ChevronRight, Flame, ShieldCheck, Maximize2, Minimize2, Search
} from 'lucide-react';

// ══════════════════════════════════════════════════════════════
//  JAISWAL CAPITAL — ALGORITHMIC DEPLOYMENT HUB v7.0 UNIFIED
//
//  ✨ NEW IN v7.0:
//   🔗 MAMTA AI PRO bot auto-sync (single source of truth)
//        Mamta AI mein banaya bot → LiveTrading mein auto-appear
//   📦 Minimizable Live Signal Stream (floating widget)
//   🏷️ Bot-source badges (MAMTA AI / WEBHOOK / MANUAL)
//   🔄 Cross-tab sync via storage events
//   🎯 Smart filter — show only specific bot's bots
//
//  ✅ INHERITED FROM v6.0:
//   🎯 Smart Option Auto-Pricer (TradingView signal → real CE/PE LTP)
//   📊 Live Option Chain panel (PCR, Max Pain, ATM auto-detect)
//   🔥 Auto-Pick best strike (ATM/OTM1/ITM1) based on bot config
//   ⚡ One-click Smart Test (BUY CE → Auto picks strike + real LTP)
//   📡 Live Signal Stream (last 20 webhook signals real-time)
//   🛡️ Smart Risk Guard (margin check, max loss, daily caps)
//   🔔 Sound + Browser notifications on trades
// ══════════════════════════════════════════════════════════════

// Foundation (endpoints, symbol universes, types, pure helpers) now lives
// in ./liveTrading/foundation — lifted verbatim, same code, smaller address.
import {
  API_BASE_URL,
  WEBHOOK_BASE_URL,
  KEEP_ALIVE_URL,
  TRADES_SUMMARY_URL,
  TRADES_TODAY_URL,
  TRADES_OPEN_URL,
  LOT_SIZES_URL,
  ATM_URL,
  EXPIRIES_URL,
  OPTION_CHAIN_URL,
  OPTION_LTP_URL,
  SIGNALS_RECENT_URL,
  DEPTH_WS_URL,
  SIGNALS_WS_URL,
  KEEP_ALIVE_INTERVAL,
  BACKEND_SYNC_INTERVAL,
  SPOT_REFRESH_INTERVAL,
  SPOT_REFRESH_FALLBACK_MS,
  OPTION_CHAIN_INTERVAL,
  SIGNAL_STREAM_INTERVAL,
  NIFTY_50_FNO_LIST,
  NIFTY_NEXT_50_LIST,
  ALL_FNO_STOCKS,
  INDICES_LIST,
  COMMODITIES_LIST,
  ETFS,
  _normSym,
  useActiveSymbols,
  toPanelSym,
  DEFAULT_LOT_META,
  BROKERS,
  TargetAsset,
  LT_TF_CHOICES,
  LT_TF_HOME,
  Deployment,
  TradeHistory,
  Log,
  FleetClient,
  InstrumentMeta,
  MamtaBotBridge,
  BRIDGE_KEY,
  readMamtaBridgeRaw,
  OptionChainRow,
  LiveSignal,
  DepthLevel,
  MarketDepthData,
  AI_MODELS,
  fmtM,
  pCol,
  pSgn,
  pnlPct,
  fmtTime,
  fetchTimeout,
  ltBeep,
  usePausableInterval,
  generateApiKey,
  renderSymbolWithBadge,
  notify,
  PRICER_SYMBOL_GROUPS,
  PRICER_INDICES,
  sessionIso,
  sessionLabel,
} from './liveTrading/foundation';

// ══════════════════════════════════════════════════════════════
//  MAIN COMPONENT — LiveTrading v6.0
// ══════════════════════════════════════════════════════════════
// `lane` selects WHICH environment this page is showing (God 2026-08-26).
//   'paper' → DUMABATI, the paper lane. Unchanged default.
//   'kali'  → KALI, the real-money lane.
// KALI is the SAME component, not a copy. A second 6,900-line page would drift
// from this one within a week, and the two would disagree about the numbers —
// which is exactly the class of fault this system keeps producing. So the page
// is shared and only its BINDING changes: KALI pins the dashboard view to LIVE
// and locks it there, because a real-money page that can silently be showing
// paper figures is worse than no page at all.
import { KaliSecurityPanel } from './components/KaliSecurityPanel';

// The six panels now live in ./liveTrading/, one file each.
import { SmartOptionPricerPanel } from './liveTrading/SmartOptionPricerPanel';
import { RudraFeedTab } from './liveTrading/RudraFeedTab';
import { LiveSignalStreamPanel } from './liveTrading/LiveSignalStreamPanel';
import { MarketDepthPanel } from './liveTrading/MarketDepthPanel';
import { SymbolControlStrip } from './liveTrading/SymbolControlStrip';
import { SymbolControlAccordion } from './liveTrading/SymbolControlAccordion';
import { ExecutionConsolePanel } from './liveTrading/ExecutionConsolePanel';
import { StrategyCardList } from './liveTrading/StrategyCardList';
import { OpenPositionsPanel } from './liveTrading/OpenPositionsPanel';
import { HeaderStatsBar } from './liveTrading/HeaderStatsBar';
import { KillSwitchPanel } from './liveTrading/KillSwitchPanel';
// M-H6 (KALI mobile audit 2026-10-04): both modals render nothing until opened (their own
// first line is `{showModal && ...}` / `{xray && ...}`), so they are fetched on first open
// instead of riding in the entry chunk.
const DeployModal = React.lazy(() => import('./liveTrading/DeployModal').then(m => ({ default: m.DeployModal })));
const SmcXrayModal = React.lazy(() => import('./liveTrading/SmcXrayModal').then(m => ({ default: m.SmcXrayModal })));
// M-H6: renders nothing until opened (`{showHistoryModal && ...}`), fetched on first open.
const TradeHistoryModal = React.lazy(() => import('./liveTrading/TradeHistoryModal').then(m => ({ default: m.TradeHistoryModal })));
import { WebhookModal } from './liveTrading/WebhookModal';
import { PageHeader } from './liveTrading/PageHeader';
import { BotActionSheet } from './liveTrading/BotActionSheet';
import { LiveConfirmModal } from './liveTrading/LiveConfirmModal';
// Deep P&L drawer (God 2026-08-27). LAZY on purpose: it reads PostgreSQL for
// the full history, and neither that code nor that query should cost anything
// on a page load where nobody opens it.
const PnlDeepDrawer = lazy(() => import('./components/PnlDeepDrawer'));
// SKIPPED CENSUS LEDGER (God 2026-10-05 18:04, dual-environment mandate): Symbol · TF · Reason of this lane's skips.
const SkippedCensusLedger = lazy(() => import('./liveTrading/SkippedCensusLedger'));
// God Phase 3 (2026-09-24): live CONTRACT prices for the open rows, the one
// P&L arithmetic module, and the CHART quick-action drawer (lazy — it mounts a
// real SuperChart pane and must cost nothing until the first click).
import { useContractLtps, ltpTargetOf } from './liveTrading/useContractLtps';
import { sumM2M } from './liveTrading/pnlMath';
import { chartTargetForRow, chartTargetForPinpoint, type ChartTarget } from './liveTrading/chartTarget';
import { contractParts } from './liveTrading/pnlMath';
const ContractChartDrawer = lazy(() => import('./liveTrading/ContractChartDrawer'));

// Last /atm fallback poll per symbol (see refreshSpots, 2026-09-25).
const _atmLastPoll = new Map<string, number>();

export const LiveTrading: React.FC<{
  onOpenChart?: (symbol: string, model?: string) => void;
  lane?: 'paper' | 'kali';
}> = ({ onOpenChart, lane = 'paper' }) => {
  const IS_KALI = lane === 'kali';
  // Shared viewport store (blueprint Phase 2) — one listener for the console,
  // and every component in a render pass agrees on the breakpoint. `isPhone` is
  // the narrow one (<640): the deployed-strategies grid and the trade tables
  // collapse there, while a tablet keeps the two-column layout.
  const { isMobile, isPhone } = useViewport();
  const [masterSwitch, setMasterSwitch] = useState(() => localStorage.getItem('jc_master_switch') === 'true');
  // THE LANE IS THE MODE — derived, never remembered (God 2026-09-04).
  //
  // KALI has always been pinned to LIVE. DUMABATI used to seed this from
  // localStorage because a header toggle could change it. That toggle was
  // removed on 2026-09-02 (a one-click route to LIVE VIEW sat inches from
  // KILL ALL), but the value it had already written stayed behind — so a
  // browser that had ever been switched to LIVE kept reading the LIVE ledger
  // on the PAPER page, with no control left anywhere to correct it. The fleet
  // is 100% PAPER, so the LIVE ledger is legitimately empty: every stat tile
  // read "Today's PnL [LIVE] +₹0 · All-time ₹0 · Trades 0 · Win Rate —" while
  // the bot cards right below showed the real -₹5.59L / 634 trades.
  //
  // Same class as the 2026-09-02 fleet-counter fix (CANON_MAI): a hand-kept
  // value shadowing the derived truth. Derive it from the lane, and a stale
  // key can never shadow it again.
  const [tradingMode, setTradingMode] = useState<'PAPER'|'LIVE'>(() =>
    IS_KALI ? 'LIVE' : 'PAPER');

  const [backendConnected, setBackendConnected] = useState(false);
  const [instrumentMeta, setInstrumentMeta] = useState<Record<string, InstrumentMeta>>(DEFAULT_LOT_META);
  const [livePrices, setLivePrices] = useState<Record<string, number>>({});
  // Live unrealized P&L per OPEN position (keyed by trade id) — polls the option
  // premium (/option-ltp) so the OPEN POSITIONS card can show a live profit/loss.
  const [openPnl, setOpenPnl] = useState<Record<string, { ltp: number; pnl: number }>>({});

  // 🆕 v6.0 state
  const [showOptionPricer, setShowOptionPricer] = useState(false);
  const [pricerSymbol, setPricerSymbol] = useState('NIFTY');
  const [showSignalStream, setShowSignalStream] = useState(false);
  const [signalStreamMinimized, setSignalStreamMinimized] = useState(false);  // 🆕 v7.0
  const [liveSignals, setLiveSignals] = useState<LiveSignal[]>([]);

  // ⭐ v11.0 — Market Depth panel state
  const [showDepthPanel, setShowDepthPanel] = useState(false);
  const [depthSymbol, setDepthSymbol] = useState('NIFTY');
  const [depthFocus, setDepthFocus] = useState<{ underlying?: string; side?: 'CE'|'PE'; strike?: number } | null>(null);

  // Build the picker-focus from a bot: open option → its underlying/side/strike (parsed
  // from "NIFTY 50 23200 CE"); else the bot's basket symbol. Keeps DEPTH = bot's symbol.
  const focusForBot = (d: Deployment): { underlying?: string; side?: 'CE'|'PE'; strike?: number } => {
    const bd = botDepth[d.id];
    if (bd?.option_symbol) {
      const t = String(bd.option_symbol).trim().split(/\s+/);
      const side = (t[t.length - 1] === 'PE' ? 'PE' : 'CE') as 'CE'|'PE';
      const strike = Number(t[t.length - 2]) || undefined;
      const underlying = t.slice(0, -2).join(' ');   // "NIFTY 50"
      return { underlying, side, strike };
    }
    return { underlying: (d.assets?.[0]?.baseSymbol || d.assets?.[0]?.symbol || 'NIFTY') };
  };
  const openBotDepth = (d: Deployment) => {
    const bd = botDepth[d.id];
    setDepthFocus(focusForBot(d));
    if (bd?.security_id) setDepthSymbol(String(bd.security_id));          // jump to live option book
    // "NIFTY 50"/"BANK NIFTY" (deployment naam) → depth-catalog spelling
    else setDepthSymbol(toPanelSym(d.assets?.[0]?.baseSymbol || d.assets?.[0]?.symbol || 'NIFTY'));
    setShowDepthPanel(true);
  };

  // ✅ v9.0 NEW STATE
  const socketRef                                             = useRef<Socket | null>(null);
  const [socketConnected, setSocketConnected]                = useState(false);
  // Kill-switch state persists across reloads (masterSwitch pehle se persist
  // tha, ye nahi — kill ke baad reload par UI ARMED dikhta tha jab tak socket
  // dobara emit na kare). Server ka KILL_SWITCH_RESET event ise false karta hai.
  const [killSwitchActive, setKillSwitchActive]              = useState(() => localStorage.getItem('jc_kill_active') === 'true');
  const [showKillPanel, setShowKillPanel]                    = useState(() => localStorage.getItem('jc_kill_active') === 'true');
  const [expiryRolled, setExpiryRolled]                      = useState(false);
  const [enableNotifications, setEnableNotifications] = useState(false);
  useEffect(() => {
    try { localStorage.setItem('jc_kill_active', String(killSwitchActive)); } catch {}
  }, [killSwitchActive]);

  // 🆕 v7.0 — Mamta AI Bot Bridge state
  const [mamtaBots, setMamtaBots] = useState<MamtaBotBridge[]>(() => {
    try { const s = readMamtaBridgeRaw(); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [showOnlySource, setShowOnlySource] = useState<'all'|'mamta_ai'|'manual'|'webhook'|'positions'|'traffic'|'promotion'>('all');

  const [deployments, setDeployments] = useState<Deployment[]>(() => {
    const saved = localStorage.getItem('jc_deployments_v3');
    if (saved) return JSON.parse(saved);
    // CRITICAL: previously this seeded a hardcoded apiKey shipped to every
    // install — anyone with the bundled JS could fire webhook signals to any
    // user's bot. Now we mint a fresh per-install key from CSPRNG.
    return [
      {
        id:'bot_seed_1', name:'TRADINGVIEW WEBHOOK — External Signals',
        assets:[], model:'WEBHOOK', side:'BOTH', orderType:'MARKET', product:'NRML',
        targetClients:['ALL'], targetBrokers:['ZERODHA','DHAN'],
        capitalAllocatedPct:25, maxLossPerDay:50000,
        activationTime:'09:15', autoStopTime:'15:20',
        reEntryEnabled:true, reEntryCount:3, trailingSlEnabled:true, squareOffOnTarget:true,
        status:'RUNNING', pnl:0, trades:0, wins:0, losses:0,
        lastSignal:'WAITING', startedAt:'09:15:00',
        webhookEnabled:true, webhookUrl:WEBHOOK_BASE_URL,
        apiKey:generateApiKey(), apiSecret:'',
        autoStrikeEnabled:true, autoFireEnabled:true, tradingMode:'PAPER'
      }
    ];
  });

  const [logs, setLogs] = useState<Log[]>(() => {
    try {
      const s = localStorage.getItem('jc_logs_v3');
      if (!s) return [];
      // Cap initial state at 300 entries — a long-running install can
      // accumulate thousands of log lines on disk and previously rehydrated
      // ALL of them into memory, dragging the console scroll to a crawl.
      const parsed = JSON.parse(s) as Log[];
      return Array.isArray(parsed) ? parsed.slice(-300) : [];
    } catch { return []; }
  });
  const [paperTrades, setPaperTrades] = useState<TradeHistory[]>(() => {
    const s = localStorage.getItem('jc_paper_trades'); return s ? JSON.parse(s) : [];
  });
  const [liveTrades, setLiveTrades] = useState<TradeHistory[]>(() => {
    const s = localStorage.getItem('jc_live_trades'); return s ? JSON.parse(s) : [];
  });

  const [fleetClients, setFleetClients] = useState<FleetClient[]>(() => {
    // Prefer jaiswal_fleet_clients (source of truth set by ClientPortfolio)
    const primary = localStorage.getItem('jaiswal_fleet_clients');
    if (primary) {
      try {
        const parsed = JSON.parse(primary);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((c: Record<string,unknown>) => ({
            id: String(c.id||''),
            name: String(c.name||''),
            capital: Number(c.capital??0),
            broker: String(c.broker||'DHAN'),
            brokerId: String(c.broker||'DHAN'),
            connected: c.status==='ACTIVE'||Array.isArray(c.brokerAccounts)&&(c.brokerAccounts as Array<{status:string}>).some(b=>b.status==='CONNECTED'),
          }));
        }
      } catch { /* ignore */ }
    }
    const s = localStorage.getItem('jc_global_clients_v2');
    if (s) { try { return JSON.parse(s); } catch { /* ignore */ } }
    return [
      { id:'master_fund', name:'Jaiswal Master Fund (Prop)', capital:0, broker:'DHAN HQ + Zerodha', brokerId:'DHAN', connected:true },
    ];
  });

  // Fetch real Dhan balance on mount and update master_fund capital
  useEffect(() => {
    const { signal, clear } = fetchTimeout(8000);
    fetch(`${API_BASE_URL}/client/master/margin`, { signal })
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (!j) return;
        const bal = j.available_balance ?? j.sod_limit ?? 0;
        if (bal > 0) {
          setFleetClients(prev => prev.map(c =>
            c.brokerId === 'DHAN' ? { ...c, capital: bal } : c
          ));
        }
      })
      .catch(() => { /* offline — keep cached */ })
      .finally(clear);
  }, []);

  // Hydrate the target-client picker from Mongo (/api/clients) — the SAME
  // collection the backend fan-out (fanOutToAllBrokers) reads. localStorage is
  // per-device; this makes the "SELECT TARGET CLIENTS" list match server truth,
  // so a client created on another device (or synced by Client Portfolio) still
  // appears here and can be targeted. Merge (don't clobber) with cached fleet.
  // Callable — the 🔄 SYNC button in SELECT TARGET CLIENTS re-runs it on demand.
  const [clientSyncBusy, setClientSyncBusy] = useState(false);
  const refreshFleetClients = useCallback(() => {
    setClientSyncBusy(true);
    const { signal, clear } = fetchTimeout(8000);
    // /api/clients is now admin-gated + credential-sanitized (returns a `brokers`
    // presence array, never raw tokens). Send the admin key; fall back to cached
    // localStorage fleet if not set / unauthorized.
    const adminKey = (()=>{ try { return localStorage.getItem('jaiswal_admin_key') || ''; } catch { return ''; } })();
    if (!adminKey) { clear(); setClientSyncBusy(false); return; }
    fetch(`${API_BASE_URL}/api/clients`, { signal, headers: { 'X-Admin-Key': adminKey } })
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        const rows = Array.isArray(j?.clients) ? j.clients : [];
        if (!rows.length) return;
        setFleetClients(prev => {
          const byId = new Map(prev.map(c => [c.id, c]));
          for (const c of rows) {
            const id = String(c.id || c._id || '');
            if (!id) continue;
            // Prefer the server's sanitized `brokers` array (tokens are stripped).
            const brokers: string[] = Array.isArray(c.brokers) && c.brokers.length ? c.brokers
              : (c.dhanClientId ? ['DHAN'] : []);
            const brokerLabel = brokers.length ? brokers.join('+') : String(c.broker || 'DHAN');
            const existing = byId.get(id);
            byId.set(id, {
              id,
              name: String(c.name || existing?.name || id),
              capital: Number(c.capital ?? existing?.capital ?? 0),
              broker: brokerLabel,
              brokerId: (brokers[0] || String(c.broker || 'DHAN')).toUpperCase(),
              // order-ready = ACTIVE with at least one live broker (server-computed)
              connected: c.orderReady ?? (String(c.status || '').toUpperCase() === 'ACTIVE' && brokers.length > 0),
            });
          }
          return Array.from(byId.values());
        });
      })
      .catch(() => { /* offline — keep localStorage fleet */ })
      .finally(() => { clear(); setClientSyncBusy(false); });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { refreshFleetClients(); }, [refreshFleetClients]);

  // Persistence — debounced so a burst of log lines (or rapid state changes
  // during the open/close auction) doesn't thrash the disk hundreds of times
  // per second. Flush still happens fast enough (300ms) that a tab close
  // never loses more than the last few state mutations.
  useEffect(() => { localStorage.setItem('jc_master_switch', String(masterSwitch)); }, [masterSwitch]);
  // `jc_trading_mode` is no longer read — the lane decides (see tradingMode
  // above). Clear it once so a browser carrying the old LIVE value stops
  // holding a landmine that a future reader would mistake for live state.
  useEffect(() => { try { localStorage.removeItem('jc_trading_mode'); } catch { /* private mode */ } }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem('jc_deployments_v3', JSON.stringify(deployments)); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [deployments]);
  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem('jc_logs_v3', JSON.stringify(logs.slice(-300))); } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [logs]);
  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem('jc_paper_trades', JSON.stringify(paperTrades)); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [paperTrades]);
  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem('jc_live_trades', JSON.stringify(liveTrades)); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [liveTrades]);

  // The TradingView webhook token the BACKEND validates. We sign every webhook
  // payload / order execution with this (not a random per-bot apiKey) so signals
  // are no longer rejected with "Invalid auth_token".
  const [tvToken, setTvToken] = useState('');
  useEffect(() => {
    // Authenticate with the stored admin key (set in API Management) — the
    // session cookie alone often isn't present cross-subdomain, which left
    // tvToken empty and the webhook payload showing a wrong (random) token.
    // Send the SESSION BEARER too (God 2026-09-05). validate_admin accepts a
    // cookie, X-Session-Token, Authorization: Bearer, OR X-Admin-Key — but this
    // call only ever sent X-Admin-Key, and that key is set by hand in API
    // Management. With no key set the request carried NO credential at all, so
    // it 401'd on every mount, silently fell back to bot.apiKey, and tripped the
    // global "Admin session expired" banner over a perfectly live session.
    // A logged-in admin already holds jc_auth_token; use it.
    const adminKey = localStorage.getItem('jaiswal_admin_key') || '';
    const bearer = (() => { try { return localStorage.getItem('jc_auth_token') || ''; } catch { return ''; } })();
    const tvHeaders: Record<string, string> = {};
    if (adminKey) tvHeaders['X-Admin-Key'] = adminKey;
    if (bearer) tvHeaders['Authorization'] = 'Bearer ' + bearer;
    fetch(`${API_BASE_URL}/admin/tv-token`, {
      credentials: 'include',
      headers: tvHeaders,
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.token) setTvToken(d.token); })
      .catch(() => { /* falls back to bot.apiKey */ });
  }, []);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [pnlDrawerOpen, setPnlDrawerOpen] = useState(false);
  const [skippedLedgerOpen, setSkippedLedgerOpen] = useState(false);   // God 2026-10-05: skipped census ledger drawer
  // Phase 5 — grouped period report (Day/Week/Month/Year) from the canonical ledger.
  // 'grouped' = hierarchical Bot→Symbol→TF→Client report (God/Subhas 2026-07-23),
  // built client-side; the default view. 'none' = flat; day/week/… = backend roll-ups.
  const [groupBy, setGroupBy] = useState<'none'|'grouped'|'day'|'week'|'month'|'year'>('grouped');
  // Collapsed accordion sections in the hierarchical report (key = bot:MODEL / sym:MODEL|SYMBOL).
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (k: string) => setCollapsedGroups(p => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  // SMC X-Ray (God/Subhas 2026-07-23): the 12-TF vault snapshot for one trade.
  const [xray, setXray] = useState<{ trade: any; loading: boolean; err: string; data: any } | null>(null);
  // 👥 per-symbol client routing — which basket row's client panel is expanded
  const [assetClientPanel, setAssetClientPanel] = useState<number | null>(null);
  const [reportGroups, setReportGroups] = useState<any[]>([]);
  const [reportGrand, setReportGrand] = useState<any>(null);
  // KALI opens on LIVE. The old unconditional 'PAPER' default meant the
  // real-money trade report opened showing paper trades (God 2026-08-26).
  const [reportTab, setReportTab] = useState<'PAPER'|'LIVE'|'OPEN'>(
    lane === 'kali' ? 'LIVE' : 'PAPER');
  const [dateFilter, setDateFilter] = useState('ALL');
  const [botFilter, setBotFilter]   = useState('ALL');   // per-bot Trade Report filter
  const [symbolFilter, setSymbolFilter] = useState('ALL'); // Trade Report: filter by symbol (God 2026-07-28)
  const [tfFilter, setTfFilter]         = useState('ALL'); // Trade Report: filter by timeframe/model
  const [showModal, setShowModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [selectedBotForWebhook, setSelectedBotForWebhook] = useState<string|null>(null);
  const [editingBotId, setEditingBotId] = useState<string|null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string|null>(null);
  const [logFilter, setLogFilter] = useState('ALL');
  // Console declutter (God layout R&D 2026-07-15): KEEP_ALIVE pings har 10 min
  // console bhar dete the — default HIDDEN, 🏓 toggle se wapas.
  const [showPings, setShowPings] = useState(false);
  const [toast, setToast] = useState('');
  const consoleRef = useRef<HTMLDivElement>(null);

  interface BackendSummary { running_bots: number; total_pnl: number; trades_today: number; win_rate: number; wins: number; losses: number; open_positions: number; last_updated: string; }
  const [backendSummary, setBackendSummary] = useState<BackendSummary | null>(null);
  const [backendOpenTrades, setBackendOpenTrades] = useState<any[]>([]);
  // Live unrealized P&L for OPEN positions. Uses /options/chain (the PUBLICLY
  // proxied endpoint — /option-quote & /option-ltp are NOT reachable through the
  // gateway, they 404). One chain fetch per underlying, then mark each open
  // option's current premium to its entry: BUY (ltp-entry)*qty, SELL inverts.
  // Polls every 6s; a row with no chain match just shows no live P&L.
  useEffect(() => {
    if (!backendOpenTrades.length) { setOpenPnl({}); return; }
    let alive = true;
    // SINGLE SOURCE OF TRUTH (God 2026-08-03): client-side chain/quote P&L
    // math REMOVED — OPEN POSITIONS card ab SIRF bridge /rag/positions se
    // bind hota hai (ledger-truth via BHUVANESHWARI gateway, ek canonical
    // server-side computation — GranularPositionManager wali HI feed, isliye
    // dono panels HAMESHA same number dikhate hain). Bridge unreachable =>
    // honest "P&L …" — UI kabhi khud compute nahi karta.
    const pull = async () => {
      try {
        // AWS migration (God 2026-08-03): positions SSOT now served from EC2 bridge.
        const r = await fetch(`https://api.jaiswalcapital.com/bridge/rag/positions?t=${Date.now()}`,
          { cache: 'no-store', signal: AbortSignal.timeout(6000) });
        const d = await r.json();
        if (!alive) return;
        if (r.ok && d?.ok) {
          const next: Record<string, { ltp: number; pnl: number }> = {};
          for (const l of (d.legs || [])) {
            if (l?.id && l.pnl != null && l.ltp != null) next[l.id] = { ltp: l.ltp, pnl: l.pnl };
          }
          setOpenPnl(next);
        } else setOpenPnl({});
      } catch { if (alive) setOpenPnl({}); }
    };
    pull();
    const iv = setInterval(pull, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [backendOpenTrades]);
  // Theme: AUTO (system) by default via prefers-color-scheme; manual override persists.
  const [themeMode, setThemeMode] = useState<'auto' | 'light' | 'dark'>(() => {
    try { return (localStorage.getItem('jc_theme') as any) || 'auto'; } catch { return 'auto'; }
  });
  useEffect(() => {
    try { localStorage.setItem('jc_theme', themeMode); } catch {}
    document.documentElement.setAttribute('data-jc-theme', themeMode);
  }, [themeMode]);
  // The light-mode invert is applied to <html> only while this page is mounted
  // (see BotActionSheet's THEME block: a filter on .lt-root broke position:fixed).
  useEffect(() => {
    document.documentElement.classList.add('jc-lt-on');
    return () => { document.documentElement.classList.remove('jc-lt-on'); };
  }, []);
  // Mobile ••• overflow menu (per-bot bottom sheet: WEBHOOK / PRICER / TEST).
  const [botMenuId, setBotMenuId] = useState<string | null>(null);
  // Phase 6 — true signal accounting (every signal EXECUTED or SKIPPED+reason).
  const [sigCounts, setSigCounts] = useState<{ signals: number; executed: number; skipped: number; buy: number; sell: number; exit: number } | null>(null);
  // epoch ms of the last successful /trades/summary sync — printed on the
  // header strip so a frozen number is never mistaken for a live one
  const [lastSyncAt, setLastSyncAt] = useState<number>(0);
  const [sigLog, setSigLog] = useState<any[]>([]);
  // Per-bot stats from the canonical ledger (keyed by botId) — real numbers for
  // the deployed-strategy cards (no static/seed values).
  const [botStats, setBotStats] = useState<Record<string, { totalTrades: number; wins: number; losses: number; netPnl: number; winRate: number }>>({});
  // THE SAME WINDOW THE HEADER LEADS WITH (God 2026-09-21, "top bar aur bot
  // cards ke beech data mismatch hai"). The header's big number is TODAY and the
  // cards' big number was the whole book since the stats epoch — both correct,
  // neither labelled, so a +₹7.7K header sat above a −₹74.0K card and the two
  // read as a contradiction. Cards now lead with today and carry the lifetime
  // underneath, exactly like the header.
  const [botStatsToday, setBotStatsToday] = useState<Record<string, { totalTrades: number; wins: number; losses: number; netPnl: number; winRate: number }>>({});
  const [botStatsLoaded, setBotStatsLoaded] = useState(false);   // server answered once -> cards never fall back to cached counters
  // Live order-book pressure per bot (the option each bot currently holds) — from /api/depth/intel
  const [botDepth, setBotDepth] = useState<Record<string, { bias: string; buyPct: number; option_symbol: string; security_id: number }>>({});
  const [currentExpiry, setCurrentExpiry] = useState('');
  // RAG Phase-3 (God 2026-07-24): server-computed ALL-TIME aggregates per mode
  // (Mongo $group) — top cards read these so the windowed ledger slice can never
  // silently shrink the "All-time" numbers (§1.4 honesty).
  const [ledgerAll, setLedgerAll] = useState<Record<string, { totalTrades: number; wins: number; winRate: number; netPnl: number }>>({});
  // THE SERVER'S OWN STATUS PER BOT, KEPT SEPARATELY (God 2026-09-21, "bots
  // running 7 hay or show 6 ho rahi hay"). The merge into `deployments` below
  // already prefers server status, but it merges into per-device localStorage —
  // so ONE card carrying a stale STOPPED (KILL ALL writes STOPPED for every bot)
  // is enough to make the header disagree with the engine, and nothing says so.
  // Mongo and /deployments both reported 7 RUNNING while the header read 6.
  // Holding the server's answer unmixed means the count can be taken from the
  // authority (RULE #29) instead of from a cache that may have out-voted it.
  const [srvBotStatus, setSrvBotStatus] = useState<Record<string, string>>({});

  // ══════════════════════════════════════════════════════════════════════════
  //  LANE SEGREGATION (God 2026-08-26, CRITICAL — real-money page showed paper)
  //
  //  God caught PAPER bots and "PAPER" badges on the KALI page. The cause is not
  //  the extracted panels — they render whatever array they are handed, and they
  //  were handed the WHOLE list. Both pages mount this same component, read the
  //  same lane-agnostic localStorage (jc_deployments_v3, jc_mamta_bots) and fetch
  //  the same API, so KALI was showing the union of both lanes and only its
  //  COLOURS said "real money".
  //
  //  The backend has had lane separation all along — kali-headless reports
  //  "LANE=LIVE (KALI · REAL MONEY): 0 bot(s) in this lane, 7 skipped as the
  //  other lane's". Only the UI never learned it.
  //
  //  Filter HERE, once, at the boundary. Every panel then gets its lane's data
  //  by construction, and no panel needs to know which lane it is in — a rule
  //  enforced in one place cannot be forgotten in the next component someone
  //  extracts. State stays whole so writes (toggle/edit/delete by id) still work.
  //
  //  A bot with no explicit tradingMode counts as PAPER. That default is
  //  deliberate: an unlabelled bot must never be assumed to be spending money.
  // ══════════════════════════════════════════════════════════════════════════
  const LANE_MODE: 'PAPER' | 'LIVE' = IS_KALI ? 'LIVE' : 'PAPER';
  const _isLaneMode = (m: any) => ((String(m || 'PAPER').toUpperCase() === 'LIVE') ? 'LIVE' : 'PAPER') === LANE_MODE;

  // KALI LISTS THE WHOLE INTENDED FLEET (God 2026-08-28). It used to drop every
  // non-LIVE bot, so with the fleet 100% paper the real-money page had nothing
  // on it at all and the operator lost the fleet view entirely.
  //
  // What must NOT come back is the 2026-08-26 defect: a paper bot wearing a
  // RUNNING/PAPER badge on the real-money page. So a bot that is not LIVE is
  // listed and FLAGGED — laneBotView decides, in one place, shared with the
  // chart. Only `deployments` widens here; laneOpenTrades and the report book
  // below stay LIVE-only, so no paper fill is ever totalled on this page.
  const laneDeployments = useMemo(
    () => deployments
      .map(d => ({ d, view: laneBotView(d.tradingMode, IS_KALI ? 'kali' : 'paper') }))
      .filter(x => x.view.visible)
      .map(({ d, view }) => (view.pending
        ? { ...d, pendingApproval: true, pendingLabel: view.statusLabel }
        : d)),
    [deployments, LANE_MODE, IS_KALI],
  );
  const laneOpenTrades = useMemo(
    () => backendOpenTrades.filter((t: any) => _isLaneMode(t?.mode ?? t?.tradingMode)),
    [backendOpenTrades, LANE_MODE],
  );
  // ── LIVE CONTRACT PRICES + RUNNING M2M (God Phase 3, 2026-09-24) ─────────
  // Every open row is priced on the contract it holds (option premium / futures
  // / stock) over the shared WS push, the per-underlying option-chain cache, or
  // — last — the bridge leg price already in `openPnl`. The M2M is
  // pnlMath.sumM2M, the same arithmetic the Open Positions panel and the
  // Granular Position Manager use, so the header and the panels agree by
  // construction. `m2m === null` = open rows exist but none is priced yet.
  const ltpTargets = useMemo(() => laneOpenTrades.map((t: any) => ltpTargetOf(t)), [laneOpenTrades]);
  const liveLtps = useContractLtps(ltpTargets, openPnl);
  const openM2M = useMemo(
    () => sumM2M(laneOpenTrades, (r: any) => liveLtps[String(r?.id)]?.ltp),
    [laneOpenTrades, liveLtps],
  );
  // CHART quick action (item 12): the contract/TF the drawer is showing.
  const [chartTarget, setChartTarget] = useState<ChartTarget | null>(null);
  // The report's own PAPER/LIVE tab is meaningless inside a lane — the lane has
  // already decided. OPEN stays selectable because it is a view, not a mode.
  const laneReportTrades = IS_KALI ? liveTrades : paperTrades;

  // LiveSignal carries no mode, but it carries bot_id, so the lane is derivable
  // exactly. A signal from a bot this client has never heard of is shown on
  // PAPER only — an unattributable signal must never surface on the page that
  // spends real money.
  const laneSignals = useMemo(() => {
    const mine = new Set(laneDeployments.map(d => d.id));
    const known = new Set(deployments.map(d => d.id));
    return liveSignals.filter(s => {
      const id = String(s.bot_id || '');
      if (mine.has(id) || mine.has('mai_' + id)) return true;
      const isKnown = known.has(id) || known.has('mai_' + id);
      return !isKnown && !IS_KALI;
    });
  }, [liveSignals, laneDeployments, deployments, IS_KALI]);

  const activeTrades = tradingMode === 'PAPER' ? paperTrades : liveTrades;
  // A pending bot is NOT running here, whatever its paper status says. This is
  // what keeps the KALI header honest (RUNNING BOTS / TODAY'S PNL) now that the
  // fleet is listed: visible is not the same as armed.
  // THE SERVER DECIDES WHETHER A BOT IS RUNNING; the card may not out-vote it.
  // `d.status` is merged state and can hold a stale STOPPED from this device's
  // localStorage, which is the 6-vs-7 God caught (7 RUNNING in Mongo and in
  // /deployments, 6 on the header). Where the server knows the bot, its answer
  // is used; where it does not (a bridge card before its first START) the local
  // status still stands, because that is the only status that exists.
  const runningDeps  = laneDeployments.filter(d => {
    const st = srvBotStatus[d.id] || d.status;
    return st === 'RUNNING' && !(d as any).pendingApproval;
  });
  // DRIFT MUST BE VISIBLE, NOT JUST CORRECTED (RULE #44b) — reported by name in
  // the effect below `addLog`, which cannot be called before it is declared.
  const _driftRef = useRef('');
  // REAL executed trades only — the top cards must mirror the Detailed Trade
  // Report (source of truth), never the bots' demo/seed stats. Stays 0 until
  // signals actually execute, then shows genuine numbers.
  // All-time numbers prefer the server $group (exact forever); fall back to the
  // client arrays only if summaryAll hasn't arrived yet (identical today).
  const _sa = ledgerAll[tradingMode];
  const totalPnl     = _sa ? _sa.netPnl : activeTrades.reduce((a,t) => a + t.pnl, 0);
  const totalTrades  = _sa ? _sa.totalTrades : activeTrades.length;          // all-time closed trades (this mode)
  const totalWins    = _sa ? _sa.wins : activeTrades.filter(t => t.status === 'WIN').length;
  const winRate      = totalTrades > 0 ? (_sa ? _sa.winRate.toFixed(1) : ((totalWins/totalTrades)*100).toFixed(1)) : '—';
  // "Trades Today" is the SESSION window (today once the market has opened, else
  // the last trading day - God 2026-09-24: at 00:38 every tile read 0 for a day
  // that had not started while the cards still showed the 22nd).
  const _istToday    = sessionIso();
  const _todayTrades = activeTrades.filter(t => t.dateIso === _istToday);
  const tradesToday  = _todayTrades.length;
  const todayPnl     = _todayTrades.reduce((a,t) => a + t.pnl, 0);   // today-only PnL (matches report's Today filter)

  const getLotSize = useCallback((symbol: string): number => {
    const sym = symbol.toUpperCase().replace('_50','').replace('_NIFTY','NIFTY').replace(/_/g,'');
    return instrumentMeta[sym]?.lot_size || DEFAULT_LOT_META[sym]?.lot_size || 75;
  }, [instrumentMeta]);

  const getStrikeStep = useCallback((symbol: string): number => {
    const sym = symbol.toUpperCase().replace('_50','').replace('_NIFTY','NIFTY').replace(/_/g,'');
    return instrumentMeta[sym]?.strike_step || DEFAULT_LOT_META[sym]?.strike_step || 50;
  }, [instrumentMeta]);

  const calcAutoStrike = useCallback((symbol: string, mode: string): number => {
    const spot = livePrices[symbol.toUpperCase()] || 0;
    if (spot <= 0) return 0;
    const step = getStrikeStep(symbol);
    const atm = Math.round(spot / step) * step;
    const offsets: Record<string, number> = { ATM: 0, ITM1: -1, ITM2: -2, ITM3: -3, OTM1: 1, OTM2: 2, OTM3: 3 };
    return atm + (offsets[mode] || 0) * step;
  }, [livePrices, getStrikeStep]);

  const emptyAsset = useCallback((): TargetAsset => ({
    symbol:'', baseSymbol:'', type:'OPTION', qty:75, lots:1, lotSize:75, sl:20, target:60,
    trailingSlPts:10, optType:'CE', expiry:'CURRENT WEEK', segment:'NSE F&O',
    strikeMode:'ATM', autoSelected:true
  }), []);

  const [newDep, setNewDep] = useState<Partial<Deployment>>({
    name:'', assets:[], model:'VEG', side:'BOTH', orderType:'MARKET', product:'NRML',
    webhookEnabled:true, targetClients:['ALL'], targetBrokers:['ZERODHA','DHAN'],
    autoStrikeEnabled:true, autoFireEnabled:true, tradingMode:'PAPER', deployTarget:'BOTH',
    capitalAllocatedPct:20, maxLossPerDay:50000, activationTime:'09:15', autoStopTime:'15:20',
    reEntryEnabled:true, reEntryCount:3, trailingSlEnabled:true, squareOffOnTarget:true,
  });

  // GLOBAL SYMBOL MANAGER = SSOT: Quick-Add dropdowns DB-driven (manager me
  // ACTIVE = yahan selectable); static lists sirf store-unreachable fallback.
  const managedSymRows = useManagedSymbols();
  const quickAddLists = useMemo(() => {
    const by = (c: string) => managedSymRows.filter(r => r.category === c).map(r => managedApiSym(r));
    const idx = by('INDEX'), etf = by('ETF'), com = by('COMMODITY');
    const stock = [...new Set([...by('CASH'), ...by('FUTURES')])].sort();
    return {
      INDEX: idx.length ? idx.map(s => s.replace(/_/g, ' ')) : ['NIFTY 50', 'BANK NIFTY', 'FIN NIFTY', 'SENSEX', 'BANKEX', 'MIDCPNIFTY'],
      STOCK: stock.length ? stock : ALL_FNO_STOCKS,
      ETF: etf.length ? etf : ETFS,
      COMMODITY: com.length ? com : COMMODITIES_LIST,
    };
  }, [managedSymRows]);

  // 🎯 PASS-TESTED SYMBOL FILTER (God rule 2026-07-13): "jo symbol test me PASS
  // hui hai wahi trade hogi" — Rudra bots ke Edit modal me Quick-Add dropdowns
  // SIRF wahi symbols dikhate hain jinke paas FINAL symbol-tagged Pine hai
  // (botversions.symbols[], non-ARCHIVED). Custom bots ka universe unchanged.
  // null = fetch fail (unknown) → full list fail-open; [] = koi tagged nahi.
  const rudraPineId = useMemo(() => {
    const m = String(newDep.model || '').toUpperCase();
    if (m === 'VEG' || m === 'SHOOL' || m === 'STHAMBH') return m.toLowerCase();
    const id = String(editingBotId || '').toLowerCase();
    const mm = id.match(/(?:^|_)(veg|shool|sthambh)$/);
    return mm ? mm[1] : '';
  }, [newDep.model, editingBotId]);
  const [passedSyms, setPassedSyms] = useState<Record<string, string[] | null>>({});
  useEffect(() => {
    // Har modal-open par refresh — PineCodePicker ka deploy() _pineVerCache
    // invalidate karta hai, isliye naya tag turant yahan bhi dikh jaata hai.
    if (!showModal || !rudraPineId) return;
    (async () => {
      try {
        let vers = _pineVerCache[rudraPineId];
        if (!vers) {
          const d = await fetch(`${API_BASE_URL}/api/bot/versions/${encodeURIComponent(rudraPineId)}`)
            .then(r => (r.ok ? r.json() : null));
          vers = Array.isArray(d?.versions) ? d.versions : [];
          _pineVerCache[rudraPineId] = vers;
        }
        const tags = new Set<string>();
        vers.filter((v: any) => v?.status !== 'ARCHIVED').forEach((v: any) => _pvSymsOf(v).forEach(s => tags.add(s)));
        setPassedSyms(p => ({ ...p, [rudraPineId]: Array.from(tags) }));
      } catch { setPassedSyms(p => ({ ...p, [rudraPineId]: null })); }
    })();
  }, [showModal, rudraPineId]);  // eslint-disable-line

  const [tempAsset, setTempAsset] = useState<TargetAsset>(emptyAsset());
  // 🔒 Segment lock guard — Rudra model chunte hi asset builder ka trade-type
  // us model ke DNA me force ho jata hai (Vega=Option, Shool=Future, Sthambh=Cash).
  useEffect(() => {
    if (!showModal) return;
    const lock = modelTypeLock(newDep.model);
    if (lock && !lock.includes(tempAsset.type)) {
      const fallback = (lock[0] === 'INDEX' ? 'OPTION' : lock[0]) as TargetAsset['type'];
      setTempAsset(p => ({ ...p, type: fallback, symbol: '' }));
    }
  }, [showModal, newDep.model]);  // eslint-disable-line react-hooks/exhaustive-deps
  // Auto-fill the bot name from trading model + trade-type until the user types
  const [nameAuto, setNameAuto] = useState(true);
  const [clientSearch, setClientSearch] = useState('');   // SELECT TARGET CLIENTS filter
  const routeAcc = useAccordion('clients');               // Edit Bot step-by-step routing accordion
  useEffect(() => {
    if (!showModal || !nameAuto) return;
    const typeLabel: Record<string, string> = { OPTION: 'Option', FUTURE: 'Futures', STOCK: 'Cash', ETF: 'ETF', COMMODITY: 'Commodity', INDEX: 'Index' };
    const tl = typeLabel[tempAsset.type] || '';
    const sym = (tempAsset.symbol || tempAsset.baseSymbol || '').toUpperCase();
    const auto = `${newDep.model || 'BOT'}${tl ? ' — ' + tl : ''}${sym ? ' ' + sym : ''}`;
    setNewDep(p => p.name === auto ? p : ({ ...p, name: auto }));
  }, [showModal, nameAuto, newDep.model, tempAsset.type, tempAsset.symbol, tempAsset.baseSymbol]);
  // Track latest toast-clear timer so a new toast at t+2.9s doesn't get
  // cleared 0.1s later by the previous toast's stale 3s timeout.
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((m: string) => {
    setToast(m);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(''), 3000);
  }, []);
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

  const addLog = useCallback((system: string, message: string, type: Log['type'] = 'INFO') => {
    setLogs(prev => [...prev.slice(-299), {
      // base36-time + random suffix — Date.now()+Math.random() previously
      // produced floats that collapsed to integers under rapid bursts, causing
      // React key collisions in the log console.
      id: `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`,
      time: new Date().toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' }),
      system, message, type,
    }]);
  }, []);

  // A CARD THAT DISAGREES WITH THE SERVER SAYS SO, ONCE, BY NAME (God
  // 2026-09-21). runningDeps already takes the server's answer; without this the
  // correction would be silent and the next stale cache would hide just as long.
  useEffect(() => {
    const drift = laneDeployments
      .filter(d => srvBotStatus[d.id] && srvBotStatus[d.id] !== d.status)
      .map(d => `${d.id} card=${d.status} server=${srvBotStatus[d.id]}`);
    const sig = drift.join('|');
    if (sig === _driftRef.current) return;
    _driftRef.current = sig;
    if (drift.length) {
      addLog('BOT_SYNC', `⚠️ ${drift.length} bot(s) disagreed with the server — `
        + `the server wins: ${drift.join(', ')}`, 'WARNING');
    }
  }, [laneDeployments, srvBotStatus, addLog]);

  useEffect(() => {
    if (consoleRef.current) consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
  }, [logs]);

  // 🆕 v6.0 — Notification permission
  // Chrome / Safari block requestPermission() unless it's tied to a user
  // gesture. Previously we called it on mount → the prompt either silently
  // failed or got auto-denied in modern browsers. Now we only read the
  // EXISTING permission on mount; the actual prompt is exposed via a button
  // (`requestNotifPermission`) that the user clicks.
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'granted') {
      setEnableNotifications(true);
    }
  }, []);
  const requestNotifPermission = useCallback(() => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') { setEnableNotifications(true); return; }
    if (Notification.permission === 'denied') {
      showToast('🔕 Notifications blocked — enable in browser site settings');
      return;
    }
    Notification.requestPermission().then(p => {
      if (p === 'granted') { setEnableNotifications(true); showToast('🔔 Notifications enabled'); }
    });
  }, [showToast]);
  // Silence unused-binding warning until a UI button wires this up.
  void requestNotifPermission;

  // KEEP-ALIVE PING
  useEffect(() => {
    const pingServer = async () => {
      try {
        const res = await fetch(`${KEEP_ALIVE_URL}?t=${Date.now()}`, { method: 'GET', cache: 'no-store' });
        if (res.ok) addLog('KEEP_ALIVE', '🏓 Server pinged', 'INFO');
      } catch (e) {
        addLog('KEEP_ALIVE', '⚠️ Keep-alive ping failed', 'WARNING');
      }
    };
    pingServer();
    const interval = setInterval(pingServer, KEEP_ALIVE_INTERVAL);
    return () => clearInterval(interval);
  }, [addLog]);

  // LOAD INSTRUMENT METADATA
  useEffect(() => {
    const loadMeta = async () => {
      try {
        const res = await fetch(`${LOT_SIZES_URL}?t=${Date.now()}`, { cache: 'no-store' });
        // Only a thrown fetch used to be reported. A 429 or a 500 returned
        // res.ok === false and this block said NOTHING — so through the
        // 2026-09-14 429 storm the console stayed silent about the failure it
        // was supposed to be announcing, and the one WARNING God did see came
        // from a network throw. Name the status, so "using defaults" is never a
        // guess about why.
        if (!res.ok) {
          addLog('META', `⚠️ Lot sizes HTTP ${res.status} — using defaults`, 'WARNING');
          return;
        }
        const data = await res.json();
        // TWO SHAPES, ONE CONTRACT (God 2026-09-17). This only ever read
        // `data.instruments`, but /lot-sizes answers
        //   {"status":"success","data":{"NIFTY":75,"FINNIFTY":40,...}}
        // — key `data`, and a BARE NUMBER per symbol, not {lot_size,...}. So
        // the guard below fired on EVERY load ("payload had no instruments"),
        // the console filled with it, and the UI silently sized every position
        // off DEFAULT_LOT_META instead of the server. Measured that day the two
        // disagreed: FINNIFTY server 40 vs default 65, BANKEX server 15 vs 30.
        // Accept both shapes; strike_step stays from defaults because this
        // endpoint does not carry it.
        const raw = data?.instruments ?? data?.data ?? null;
        if (!raw || typeof raw !== 'object') {
          addLog('META', '⚠️ Lot sizes payload had no instruments — using defaults', 'WARNING');
          return;
        }
        const meta: Record<string, { lot_size: number; strike_step: number }> = {};
        for (const [sym, v] of Object.entries(raw as Record<string, any>)) {
          const lot = typeof v === 'number' ? v : Number(v?.lot_size);
          if (!lot || !isFinite(lot)) continue;
          const step = (typeof v === 'object' && Number(v?.strike_step))
            || DEFAULT_LOT_META[sym]?.strike_step || 50;
          meta[sym] = { lot_size: lot, strike_step: step };
        }
        if (!Object.keys(meta).length) {
          addLog('META', '⚠️ Lot sizes payload had no usable rows — using defaults', 'WARNING');
          return;
        }
        // ONE LINE, AND IT ONLY SPEAKS WHEN SOMETHING IS WRONG (God 2026-09-21).
        // This block used to log TWICE per refresh: a SUCCESS line listing every
        // symbol where the server disagreed with the defaults, and a second line
        // reading "📦 Loaded undefined instrument lot sizes" — undefined because
        // /lot-sizes answers {status,data} and has no `count` field, which the
        // 09-17 fix had already discovered for `data.instruments` and missed
        // here. Worse, the disagreement it announced was real but unactionable:
        // the server said FINNIFTY 40 / BANKEX 15, the defaults said 65 / 30,
        // and the broker's master says 60 / 30 — BOTH sides were wrong and the
        // console could not say which to believe. The server now serves the
        // engine's own table, so a difference here means the two have genuinely
        // drifted and is worth a WARNING; agreement is silent.
        const changed = Object.entries(meta)
          .filter(([k, v]) => DEFAULT_LOT_META[k] && DEFAULT_LOT_META[k].lot_size !== v.lot_size)
          .map(([k, v]) => `${k} ${DEFAULT_LOT_META[k].lot_size}→${v.lot_size}`);
        const n = Number(data.count) || Object.keys(meta).length;
        setInstrumentMeta({ ...DEFAULT_LOT_META, ...meta });
        if (changed.length) {
          addLog('META', `⚠️ Lot sizes: server differs from the built-in defaults on `
            + `${changed.length} symbol(s) — using the SERVER (engine truth): ${changed.join(', ')}`,
            'WARNING');
        } else {
          addLog('META', `✅ Lot sizes loaded (${n} symbols) — server and defaults agree`, 'SUCCESS');
        }
      } catch (e) {
        addLog('META', '⚠️ Could not load lot sizes — using defaults', 'WARNING');
      }
    };
    loadMeta();
    fetch(`${EXPIRIES_URL}?symbol=NIFTY&t=${Date.now()}`, { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.current_week) setCurrentExpiry(d.current_week); })
      .catch(() => {});
  }, [addLog]);

  // LIVE SPOT PRICE REFRESH
  useEffect(() => {
    const refreshSpots = async () => {
      // FALLBACK ONLY FOR WS-SILENT SYMBOLS (2026-09-24). Measured on the live
      // box: this loop asked /atm for every deployed symbol (~55) every 30 s
      // whether or not the shared socket was already ticking it - 726 /atm
      // hits in 10 minutes from one browser, each a cache MISS on the server's
      // 30 s candle-close cache, each a multi-day CSV load: api.py at 57% CPU
      // on a 2-vCPU box while the engine's pass stretched to 8-13 minutes.
      // A symbol the fast lane ticked inside the last minute needs no REST
      // fallback; one it has not is polled exactly as before.
      // 2026-09-25: the ~30 Sthambh cash/ETF names are not on the spine at all,
      // so they never tick and were still polled every 30 s (~54 /atm per
      // minute from one browser, each a server cache miss). They are 1h/4h/1d
      // cells; a non-index symbol the socket does not carry is now polled at
      // most once per _slowPollMs. Indices keep the 30 s fallback.
      const _wsFreshMs = 60_000;
      const _slowPollMs = 120_000;
      const _now = Date.now();
      const symbols = Array.from(new Set([
        ...INDICES_LIST,
        ...deployments.flatMap(d => d.assets.map(a => (a.baseSymbol || a.symbol.split(' ')[0]).toUpperCase()))
      ])).filter(s => s && s.length > 0).filter(s => {
        const c = getLastLTP(s);
        if (c && c.ltp > 0 && _now - c.at < _wsFreshMs) return false;
        if (INDICES_LIST.includes(s)) return true;
        return _now - (_atmLastPoll.get(s) || 0) >= _slowPollMs;
      });
      if (symbols.length === 0) return;
      symbols.forEach(s => _atmLastPoll.set(s, _now));

      const results = await Promise.allSettled(
        symbols.map(async (sym) => {
          const r = await fetch(`${ATM_URL}?symbol=${sym}&t=${Date.now()}`, { cache: 'no-store' });
          if (!r.ok) return null;
          const data = await r.json();
          return data.status === 'success' ? { sym, spot: data.ltp ?? data.spot ?? 0 } : null;
        })
      );

      const updates: Record<string, number> = {};
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value) updates[r.value.sym] = r.value.spot;
      });

      if (Object.keys(updates).length > 0) {
        setLivePrices(p => ({ ...p, ...updates }));
      }
    };

    refreshSpots();

    // ── WS PUSH FOR THE DASHBOARD (God 2026-09-21, design change APPROVED) ──
    // The loop below was the WHOLE lane: one REST /atm call PER SYMBOL every 5
    // seconds. MEASURED on the live box before this change - sampling the same
    // `spine:ltp:*` mirror the dashboard reads, 60 keys over 10 seconds during
    // market hours - the median gap between updates was 1,087 ms (p90 3,187 ms)
    // and only 2.7% of updates landed inside 30 ms. The 30 ms SLA that shipped
    // 2026-08-28 lives on the /ws/live-feed PUSH, which SuperChart already
    // multiplexes; the dashboard simply was not on it.
    //
    // So every symbol now rides the SHARED socket (marketFeed ref-counts it -
    // this opens no new transport and adds no Dhan load, RULE #22), and the
    // REST loop is kept as a SLOW FALLBACK, widened from 5s to 30s, for symbols
    // the fast lane does not carry. Deleting the poll outright would blank any
    // such symbol; a slow poll under a live push can only ever fill a gap.
    const _wsOff: Array<() => void> = [];
    try {
      const _wsSyms = Array.from(new Set([
        ...INDICES_LIST,
        ...deployments.flatMap(d => d.assets.map(a => (a.baseSymbol || a.symbol.split(' ')[0]).toUpperCase())),
      ])).filter(x => x && x.length > 0);
      for (const sym of _wsSyms) {
        // warm from the cache so a freshly-mounted dashboard is not blank while
        // it waits for the symbol's next tick
        const seed = getLastLTP(sym);
        if (seed && seed.ltp > 0) setLivePrices(p => (p[sym] === seed.ltp ? p : { ...p, [sym]: seed.ltp }));
        _wsOff.push(subscribeLTP(sym, (ltp) => {
          if (!(ltp > 0)) return;          // never write a 0 over a real price
          setLivePrices(p => (p[sym] === ltp ? p : { ...p, [sym]: ltp }));
        }));
      }
    } catch { /* WS unavailable - the fallback poll below still serves prices */ }

    // God 2026-07-31 (persistent paper-test): PERSISTENT live data — spots keep
    // refreshing even when the window is not focused (hidden-pause removed) so live
    // prices never freeze while the Edit Bot / paper modal stays open.
    const id = setInterval(() => { refreshSpots(); }, SPOT_REFRESH_FALLBACK_MS);
    return () => { _wsOff.forEach(f => { try { f(); } catch { /* already gone */ } }); clearInterval(id); };
  }, [deployments]);

  // 🆕 v7.0 — MAMTA AI PRO BOT BRIDGE — auto-import bots.
  // Skips the JSON.parse + state update when the localStorage payload is byte
  // identical to the last poll. Previously the 3s poll parsed ~5KB of JSON
  // and ran the reconciliation 20 times per minute even when nothing changed.
  const lastBridgeRawRef = useRef<string>('');
  useEffect(() => {
    const importMamtaBots = () => {
      try {
        const raw = readMamtaBridgeRaw();
        if (!raw) return;
        if (raw === lastBridgeRawRef.current) return; // no-op when unchanged
        lastBridgeRawRef.current = raw;
        const incoming: MamtaBotBridge[] = JSON.parse(raw);
        if (!Array.isArray(incoming) || incoming.length === 0) return;

        setMamtaBots(incoming);

        setDeployments(prev => {
          const existingMamtaIds = new Set(prev.filter(d => d.id.startsWith('mai_')).map(d => d.id));
          const incomingIds = new Set(incoming.map(b => 'mai_' + b.id));

          // Step 1: Remove deleted Mamta bots — mai_veg_mcx EXEMPT (server-owned
          // system bot, Mamta bridge me kabhi register nahi hota; God 2026-07-26)
          let next = prev.filter(d => !d.id.startsWith('mai_') || d.id === 'mai_veg_mcx' || d.id === 'mai_shool_mcx' || incomingIds.has(d.id));

          // Step 2: Add new + update existing
          incoming.forEach(b => {
            const depId = 'mai_' + b.id;
            const exists = next.find(d => d.id === depId);
            const newDep: Deployment = exists ? {
              ...exists,
              name: b.name,
              // Run-state (RUNNING/STOPPED) is the user's EXPLICIT START/STOP in
              // Live Auto Trading — NEVER overwrite it from the Mamta bridge.
              // PAPER is a trading MODE, not "running"; the old statusMap forced
              // every paper bot to RUNNING and the 3s poll re-STARTED stopped bots
              // → "multiple bots auto connected". Only sync mode + live stats.
              tradingMode: b.status === 'live' ? 'LIVE' : 'PAPER',
              pnl: b.pnl,
              trades: b.trades,
              wins: b.wins,
              losses: b.losses,
            } : {
              id: depId,
              name: `🔱 ${b.name} (from Mamta AI)`,
              assets: [{
                symbol: b.symbol,
                baseSymbol: b.symbol.split(' ')[0].toUpperCase(),
                type: 'OPTION',
                qty: 75, lots: 1, lotSize: 75,
                sl: 20, target: 60, trailingSlPts: 10,
                optType: 'CE', expiry: 'CURRENT WEEK', segment: 'NSE F&O',
                strikeMode: 'ATM', autoSelected: true,
              }],
              model: b.module || 'VEG',
              side: 'BOTH', orderType: 'MARKET', product: 'NRML',
              targetClients: ['ALL'], targetBrokers: ['ZERODHA','DHAN'],
              capitalAllocatedPct: 20, maxLossPerDay: 50000,
              activationTime: '09:15', autoStopTime: '15:20',
              reEntryEnabled: true, reEntryCount: 3,
              trailingSlEnabled: true, squareOffOnTarget: true,
              // NEW imports start STOPPED + Auto-Fire OFF so they NEVER auto-run.
              // The user explicitly STARTS the one bot they want (mirrors the
              // RUDRA one-click deploy: "AUTO OFF — review then turn on").
              status: 'STOPPED',
              pnl: b.pnl, trades: b.trades, wins: b.wins, losses: b.losses,
              lastSignal: '—',
              startedAt: new Date().toLocaleTimeString('en-IN', { hour12: false }),
              webhookEnabled: true,
              webhookUrl: WEBHOOK_BASE_URL,
              apiKey: generateApiKey(),
              apiSecret: '',
              autoStrikeEnabled: true,
              autoFireEnabled: false,
              tradingMode: b.status === 'live' ? 'LIVE' : 'PAPER',
            };

            if (exists) {
              next = next.map(d => d.id === depId ? newDep : d);
            } else {
              next = [newDep, ...next];
              addLog('MAMTA_SYNC', `🔗 Imported bot from Mamta AI Pro: ${b.name}`, 'SUCCESS');
            }
          });

          return next;
        });
      } catch (e) {}
    };

    // Initial import
    importMamtaBots();

    // Listen for cross-tab storage events
    const handler = (e: StorageEvent) => {
      if (e.key === BRIDGE_KEY) importMamtaBots();
    };
    window.addEventListener('storage', handler);

    // Poll every 3s for same-tab updates (storage event doesn't fire same-tab)
    const id = setInterval(importMamtaBots, 3000);

    return () => {
      window.removeEventListener('storage', handler);
      clearInterval(id);
    };
  }, [addLog]);

  // 🆕 v6.0 — LIVE SIGNAL STREAM POLLING
  const fetchSignals = useCallback(async () => {
    const { signal: abortSig, clear } = fetchTimeout(5000);
    try {
      const r = await fetch(
        `${SIGNALS_RECENT_URL}?limit=20&t=${Date.now()}`,
        { cache: 'no-store', signal: abortSig },
      );
      if (!r.ok) return;
      const j = await r.json();
      // Accept multiple shapes: { signals:[…] } / { data:[…] } / { results:[…] } / bare array
      const raw: any[] = j.signals ?? j.data ?? j.results ?? j.items ?? (Array.isArray(j) ? j : []);
      if (!Array.isArray(raw)) return;
      const mapped: LiveSignal[] = raw.map((s: any, i: number) => ({
        id: s.id || s._id || `sig_${i}_${Date.now()}`,
        time: s.time || (s.created_at || s.timestamp || '').slice(11, 19) || new Date().toLocaleTimeString('en-IN', { hour12: false }),
        bot_id: s.bot_id || '',
        bot_name: s.bot_name || s.bot_id || '—',
        action: s.action || s.side || s.type || '—',
        comment: s.comment || s.message || s.remarks || '',
        symbol: s.symbol || s.underlying || s.index || '—',
        option_symbol: s.option_symbol || s.full_symbol || s.tradingsymbol || '',
        strike: s.strike ?? s.strike_price,
        opt_type: (s.option_type || s.opt_type || s.call_put) as 'CE' | 'PE',
        spot: s.spot || s.ltp_spot || s.index_ltp || s.price || 0,
        ltp: s.option_ltp || s.premium || s.ltp || 0,
        status: (s.status === 'success' || s.status === 'executed' || s.status === 'EXECUTED') ? 'EXECUTED'
          : (s.status === 'rejected' || s.status === 'failed' || s.status === 'FAILED') ? 'FAILED'
          : 'PENDING',
        pnl: s.pnl,
      }));
      setLiveSignals(mapped);
    } catch { /* silent — retry on next tick */ } finally { clear(); }
  }, []); // stable — no external deps needed; interval handles refresh

  useEffect(() => {
    fetchSignals();
    // 🔁 hidden = 5× slow (2s → 10s) — poora pause NAHI: auto-fire WS push se
    // hota hai, par list background me bhi warm rehti hai.
    let n = 0;
    const id = setInterval(() => {
      n++;
      if (document.hidden && n % 5 !== 0) return;
      fetchSignals();
    }, SIGNAL_STREAM_INTERVAL);
    return () => clearInterval(id);
  }, [fetchSignals]);

  // 🔥 AUTO-FIRE bridge — holds the latest auto-fire handler so the WS effect
  // (mounted once, empty deps) always calls the CURRENT closure (deployments,
  // masterSwitch, token) instead of a stale one.
  const autoFireRef = useRef<(s: any) => void>(() => {});

  // Real-time signal push via WebSocket — new TradingView signals appear instantly
  // without waiting for the 2s polling cycle.
  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnTimer: ReturnType<typeof setTimeout> | null = null;
    let mounted = true;

    const connect = () => {
      if (!mounted) return;
      // double-connect guard — reconnect timer + ensureAlive dono se ek hi socket
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
      try {
        ws = new WebSocket(SIGNALS_WS_URL);
        // socketConnected was declared but NEVER set (measured 2026-08-30:
        // zero call sites), so the LIVE-lane "WebSocket DROPPED — kill-switch
        // delayed" banner showed permanently regardless of the real socket.
        // A banner that is always on protects nothing and trains the eye to
        // ignore the one day it would be true.
        ws.onopen = () => { if (mounted) setSocketConnected(true); };
        ws.onmessage = (e) => {
          if (!mounted) return;
          try {
            const s = JSON.parse(e.data as string);
            if (!s.action) return;
            const newSig: LiveSignal = {
              id: `ws_${s.time}_${s.symbol}_${s.action}`,
              time: s.time || new Date().toLocaleTimeString('en-IN', { hour12: false }),
              bot_id: s.bot_id || '',
              bot_name: s.bot_name || s.bot_id || '—',
              action: s.action || '—',
              comment: s.comment || '',
              symbol: s.symbol || '—',
              option_symbol: s.option_symbol || '',
              strike: s.strike,
              opt_type: s.opt_type as 'CE' | 'PE',
              spot: s.spot || s.price || 0,
              ltp: s.ltp || 0,
              status: 'PENDING',
            };
            // Prepend instantly; HTTP polling will refresh + deduplicate within 2s.
            setLiveSignals(prev => {
              const deduped = prev.filter(x => x.id !== newSig.id);
              return [newSig, ...deduped].slice(0, 20);
            });
            // 🔥 AUTO-FIRE: a received signal must hit the broker — previously it
            // was only displayed, so nothing ever executed. The ref guards on
            // master switch + bot RUNNING + Auto-Fire ON.
            autoFireRef.current(s);
          } catch { /* ignore malformed */ }
        };
        ws.onclose = () => {
          if (mounted) { setSocketConnected(false); reconnTimer = setTimeout(connect, 5000); }
        };
        ws.onerror = () => ws?.close();
      } catch { /* no WS support */ }
    };

    // 🔁 SIGNAL-WS SELF-HEAL — laptop sleep / network flap par socket bina
    // close-event ke half-open mar jata hai → auto-fire chupchaap band. Tab
    // wapas aane / network online hone par turant readyState check + reconnect.
    const ensureAlive = () => {
      if (!mounted) return;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        try { ws?.close(); } catch { /* already dead */ }
        connect();
      }
    };
    const onVis = () => { if (!document.hidden) ensureAlive(); };
    window.addEventListener('online', ensureAlive);
    document.addEventListener('visibilitychange', onVis);

    connect();
    return () => {
      mounted = false;
      window.removeEventListener('online', ensureAlive);
      document.removeEventListener('visibilitychange', onVis);
      ws?.close();
      if (reconnTimer) clearTimeout(reconnTimer);
    };
  }, []);

  // CORE BACKEND DATA SYNC
  // Resilient: (1) an in-flight guard stops 3s cycles piling up when the ledger is
  // slow (~3s) or capture-5s spikes CPU; (2) every fetch has its own timeout and
  // NEVER throws (safeFetch → null on error), so one slow/blipping auxiliary
  // endpoint can't "drop" the whole sync; (3) connection state is driven only by
  // the core summary/ledger reachability — no more OFFLINE flapping + console spam.
  const syncInFlight = useRef(false);
  const offlineStrikesRef = useRef(0);   // consecutive failed reachability probes
  // Ids created locally in the last 2 min — the server save may still be in
  // flight, so the existence-reconcile must not drop their cards.
  const recentCreateRef = useRef<Record<string, number>>({});
  // Server deployment → local card shape (used when another tab/device created
  // a bot, or to self-heal after a transient empty list dropped cards).
  const mapServerBot = (b: any): Deployment => ({
    id: String(b.id), name: b.name || String(b.id), assets: Array.isArray(b.assets) ? b.assets : [],
    model: b.model || 'CUSTOM', side: b.side || 'BOTH', orderType: b.orderType || 'MARKET',
    product: b.product || 'NRML', targetClients: b.targetClients || ['ALL'],
    targetBrokers: b.targetBrokers || ['DHAN'], capitalAllocatedPct: Number(b.capitalAllocatedPct) || 20,
    maxLossPerDay: Number(b.maxLossPerDay) || 50000, activationTime: b.activationTime || '09:15',
    autoStopTime: b.autoStopTime || '15:20', reEntryEnabled: b.reEntryEnabled !== false,
    reEntryCount: Number(b.reEntryCount) || 3, trailingSlEnabled: b.trailingSlEnabled !== false,
    squareOffOnTarget: b.squareOffOnTarget !== false, tradeWindows: b.tradeWindows || [],
    status: String(b.status).toUpperCase() === 'RUNNING' ? 'RUNNING' : 'STOPPED',
    pnl: 0, trades: 0, wins: 0, losses: 0, lastSignal: '—', startedAt: '—',
    webhookEnabled: true, webhookUrl: b.webhookUrl || '', apiKey: b.apiKey || '', apiSecret: '',
    autoStrikeEnabled: b.autoStrikeEnabled !== false, autoFireEnabled: b.autoFireEnabled !== false,
    tradingMode: String(b.tradingMode).toUpperCase() === 'LIVE' ? 'LIVE' : 'PAPER',
    deployTarget: b.deployTarget || 'BOTH',
  });
  const fetchBackendData = useCallback(async () => {
    if (syncInFlight.current) return;             // previous cycle still running — skip
    syncInFlight.current = true;
    const safeFetch = async (url: string, ms = 6000): Promise<any | null> => {
      try {
        const r = await fetch(`${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`, { cache: 'no-store', signal: AbortSignal.timeout(ms) });
        if (!r.ok) return null;
        return await r.json().catch(() => null);
      } catch { return null; }
    };
    try {
      // Core reachability = summary (fast, tiny). Aux endpoints are independent.
      // OFFLINE-DEBOUNCE: ek slow/missed response par pura system "OFFLINE" mat
      // dikhao — API par load spike me 1 request 5s paar jana normal hai. Do
      // CONSECUTIVE failures ke baad hi drop declare hota hai ("bar bar offline"
      // flicker isi se aata tha); recovery pehle jaisi instant hai.
      const sum = await safeFetch(TRADES_SUMMARY_URL, 8000);
      if (sum !== null) {
        offlineStrikesRef.current = 0;
        setLastSyncAt(Date.now());   // the header prints this, so a stale number is never mistaken for a live one
        if (!backendConnected) { setBackendConnected(true); addLog('BACKEND', '🔗 MAMTA AI Engine v6.0 sync ONLINE', 'SUCCESS'); }
      } else if (backendConnected) {
        offlineStrikesRef.current += 1;
        if (offlineStrikesRef.current >= 2) {
          setBackendConnected(false); addLog('BACKEND', '⚠️ Sync dropped — retrying…', 'WARNING');
        }
      }

      const o = await safeFetch(TRADES_OPEN_URL, 5000);
      if (o && Array.isArray(o.trades)) setBackendOpenTrades(o.trades);

      // ── CANONICAL LEDGER — the SINGLE source for the top cards AND the Detailed
      // Trade Report, so they reconcile by construction (no more 620-vs-1388).
      // Closed trades only (WIN/LOSS/BREAKEVEN); OPEN lives in the OPEN tab. ──
      // RAG Phase-3 (God 2026-07-24): the old call re-downloaded the FULL lifetime
      // ledger every 3s. Now: 60-day window + 1200-row cap bounds the Mongo scan
      // and network churn; openAlways=1 keeps older OPEN positions (Shool positional)
      // in the slice; summaryAll=1 keeps the All-time cards exact via server $group.
      const _cutIso = new Date(Date.now() + 5.5*3600*1000 - 60*86400000).toISOString().slice(0, 10);
      const lj = await safeFetch(`${API_BASE_URL}/api/trades/ledger?from=${_cutIso}&limit=1200&openAlways=1&summaryAll=1`, 8000);
      if (lj) {
        // ACTIVE PINE (God 2026-10-12): the headline numbers are the enabled legs' current pins only; a mode with no
        // active-Pine trade yet reads 0 (never the all-time figure of failed Pines). summaryAll stays the fallback for
        // a server that predates summaryActive.
        if (lj.summaryActive) {
          const act: Record<string, any> = {};
          for (const m of Object.keys({ ...(lj.summaryAll || {}), PAPER: 1, LIVE: 1 }))
            act[m] = lj.summaryActive[m] || { totalTrades: 0, wins: 0, winRate: 0, netPnl: 0 };
          setLedgerAll(act);
        } else if (lj.summaryAll) setLedgerAll(lj.summaryAll);
        const rows: any[] = Array.isArray(lj?.trades) ? lj.trades : [];
        const mapped: TradeHistory[] = rows
          .filter(t => ['WIN', 'LOSS', 'BREAKEVEN'].includes(t.status))
          .map(t => ({
            id: t.id, tradeId: t.id, time: t.time || '', date: t.date || '', dateIso: t.dateIso || '',
            symbol: t.optionSymbol || t.symbol || '—',
            side: (t.entrySide === 'SELL' ? 'SELL' : 'BUY') as 'BUY' | 'SELL',
            qty: Number(t.qty) || 0,
            entryPrice: Number(t.entryPrice) || 0, exitPrice: Number(t.exitPrice) || 0,
            pnl: Number(t.netPnl ?? t.pnl) || 0,
            status: t.status as 'WIN' | 'LOSS' | 'BREAKEVEN',
            mode: (String(t.tradingMode).toUpperCase() === 'LIVE' ? 'LIVE' : 'PAPER') as 'PAPER' | 'LIVE',
            broker: t.broker || (String(t.tradingMode).toUpperCase() === 'LIVE' ? '—' : 'PAPER'),
            client: t.clientName || 'All Clients',
            botId: t.botId || 'unknown', botName: t.botName || t.botId || '—',
            source: t.source || t.signalSource || t.module || '',
            entryTime: t.entryTime || t.time || '', exitTime: t.exitTime || '',
            timeframe: t.timeframe || t.tf || t.entryTf || '',
            // CLIENT→BROKER→CAPITAL mapping (God 2026-07-26): stamped at fill
            capitalUsed: Number(t.capitalUsed) || 0,
            capitalBasis: String(t.capitalBasis || ''),
            // God 2026-08-08: Dhan-style instrument identity from the enriched
            // ledger proxy (scrip-master securityId join) — report modal STRIKE
            // column + exports read these; absent on legacy rows (honest blank).
            displaySymbol: String(t.displaySymbol || ''),
            brokerTradingSymbol: String(t.brokerTradingSymbol || ''),
            expiry: String(t.expiry || t.expiryDate || ''),
          }));
        setPaperTrades(mapped.filter(m => m.mode === 'PAPER'));
        setLiveTrades(mapped.filter(m => m.mode === 'LIVE'));

        // OPEN POSITIONS — derive from the SAME canonical ledger (status OPEN /
        // PARTIALLY_CLOSED) so the tab reconciles with the report. The old
        // separate TRADES_OPEN endpoint returned stale/empty → tab showed 0 even
        // with live positions. Carries the fields the per-row Square-Off needs.
        const openMapped = rows
          .filter(t => ['OPEN', 'PARTIALLY_CLOSED'].includes(t.status))
          .map(t => ({
            id: t.id, tradeId: t.id,
            symbol: t.symbol || '—',
            option_symbol: t.optionSymbol || t.symbol || '—',
            optType: (t.optType || '').toUpperCase(),
            action: (t.entrySide === 'SELL' ? 'SELL' : 'BUY'),
            quantity: Number(t.openQty ?? t.qty) || 0,
            entry_price: Number(t.entryPrice) || 0,
            botId: t.botId || '', botName: t.botName || t.botId || '—',
            mode: (String(t.tradingMode).toUpperCase() === 'LIVE' ? 'LIVE' : 'PAPER'),
            time: t.entryTime || t.time || '',
            strike: Number(t.strike ?? t.strikePrice) || 0,
            // God Phase 3 (2026-09-24): the ledger's own fields, unrenamed, so
            // pnlMath / useContractLtps / the CHART drawer read one shape.
            status: t.status, openQty: Number(t.openQty ?? t.qty) || 0, qty: Number(t.qty) || 0,
            entryPrice: Number(t.entryPrice) || 0,
            side: t.side, entrySide: t.entrySide, direction: t.direction,
            optionSymbol: t.optionSymbol || '', contractSymbol: t.contractSymbol || '',
            assetClass: t.assetClass || '', expiry: t.expiry || t.expiryDate || '',
            timeframe: t.timeframe || t.tf || t.entryTf || '',
            securityId: t.securityId || '', segment: t.segment || '',
            dnaSl: t.dnaSl ?? null, dnaTarget: t.dnaTarget ?? null,
            dnaSlPremium: t.dnaSlPremium ?? null, dnaSlPremiumBasis: t.dnaSlPremiumBasis || '',
            spotEntry: t.spotEntry ?? null, pineVersion: t.pineVersion || '',
          }));
        setBackendOpenTrades(openMapped);
      }

      // ── SERVER-AUTHORITATIVE bots — the ENGINE fires off the Node DB, so the
      // cards must mirror THAT truth (status AND existence), not localStorage.
      // (DB had mai_* RUNNING while every card showed STOPPED → 220 "phantom"
      // paper trades; deleted bots survived in the DB and kept trading.)
      // mai_* cards are bridge-owned (System Bots) — absence on the server is
      // normal until their first START upserts them, so they're never dropped.
      const depj = await safeFetch(`${API_BASE_URL}/deployments`, 6000);
      if (depj && Array.isArray(depj.deployments)) {
        const srv = new Map<string, any>(depj.deployments.filter((b: any) => b?.id).map((b: any) => [String(b.id), b]));
        setSrvBotStatus(Object.fromEntries([...srv].map(([id, b]) => [
          id, String(b?.status).toUpperCase() === 'RUNNING' ? 'RUNNING' : 'STOPPED'])));
        // 🧹 STALE BRIDGE PURGE (God 2026-07-13): jc_mamta_bots bridge PER-DEVICE
        // localStorage hai — desktop par delete kiye bots (Trend/Reversal/Option)
        // mobile ke bridge me zinda reh kar phantom mai_* cards banate the.
        // Rule: canonical Rudra trio HAMESHA rehta hai (server par first-START
        // tak absent hona normal); baaki bridge entries sirf tab jab server par
        // exist karein ya abhi-abhi (10 min) sync hui hon (naya bot in-flight).
        // mai_veg_mcx (God 2026-07-26): MCX commodity bot ab canonical system-bot —
        // server-owned (Mongo-created), bridge me kabhi nahi tha isliye pehle
        // UI-list se filter ho jaata tha (God ne pakda: 18 MCX legs UI me gayab).
        // DERIVED, not typed out (God 2026-09-01). This was a hand-kept list of
        // five and it went stale every time the fleet grew: mai_veg_mcx had to be
        // added by hand (God 2026-07-26, "18 MCX legs UI me gayab"), then
        // mai_shool_mcx, and both sell lanes were still missing — so the header
        // counted 2 RUNNING while the server had 7. botIdentity.FLEET is the
        // canonical 7 (RULE #29, "everything keys on it"); reading it here means
        // an eighth bot can never again be invisible because someone forgot a
        // string literal.
        const CANON_MAI = new Set(FLEET_REGISTRY.map(b => b.id));
        let bridgeIds: Set<string> | null = null;
        try {
          const rawB = JSON.parse(localStorage.getItem('jc_mamta_bots') || '[]');
          if (Array.isArray(rawB)) {
            const keep = rawB.filter((b: any) =>
              CANON_MAI.has('mai_' + b?.id) || srv.has('mai_' + b?.id) || srv.has(String(b?.id))
              || Date.now() - (Number(b?.syncedAt) || 0) < 600000);
            if (keep.length !== rawB.length) {
              localStorage.setItem('jc_mamta_bots', JSON.stringify(keep));
              setMamtaBots(keep);
              addLog('MAMTA_SYNC', `🧹 ${rawB.length - keep.length} stale deleted bot(s) purged — server par exist nahi karte`, 'WARNING');
            }
            bridgeIds = new Set(keep.map((b: any) => 'mai_' + b?.id));
          }
        } catch {}
        setDeployments(p => {
          let changed = false;
          const next: Deployment[] = [];
          for (const d of p) {
            const s = srv.get(d.id);
            if (s) {
              const st: 'RUNNING'|'STOPPED' = String(s.status).toUpperCase() === 'RUNNING' ? 'RUNNING' : 'STOPPED';
              // THE SERVER ALSO OWNS THE LANE (God 2026-09-22: "Sthambh LIVE
              // auto trading par hay par papar par nahi aai"). Only `status`
              // used to be taken from the server; `tradingMode` was left at
              // whatever this device had cached. laneBotView() keys visibility
              // on exactly that field, so one stale 'LIVE' in localStorage made
              // a PAPER bot vanish from DUMABATI while still listing on KALI —
              // and no refresh could ever fix it, because the server's PAPER
              // answer was never allowed to out-vote the cache. Same reasoning
              // the comment above gives for status (RULE #29: the server is the
              // authority); the lane simply never got it.
              const sm = s.tradingMode ? String(s.tradingMode).toUpperCase() : '';
              const md = sm === 'LIVE' ? 'LIVE' : (sm ? 'PAPER' : d.tradingMode);
              if (st !== d.status || md !== d.tradingMode) {
                changed = true; next.push({ ...d, status: st, tradingMode: md });
              } else next.push(d);
            } else if (d.id.startsWith('mai_')) {
              // System-Bots bridge card — server record appears on first START.
              // Canonical trio hamesha; baaki sirf jab tak cleaned bridge me hain
              // (dusre device par deleted bots ab yahan nahi bachte).
              if (CANON_MAI.has(d.id) || !bridgeIds || bridgeIds.has(d.id)) next.push(d);
              else changed = true;
            } else if (Date.now() - (recentCreateRef.current[d.id] || 0) < 120000) {
              next.push(d);        // just created locally — server save still in flight
            } else {
              changed = true;      // deleted on the server (🗑 / purge-all / other tab) → drop card
            }
          }
          for (const [id, b] of srv) {   // bots created on another device/tab
            // Server-created system bots (mai_veg_mcx God 2026-07-26, mai_shool_mcx,
            // and BOTH sell lanes) never appear in the per-device localStorage
            // bridge, so the server is the only place they can come from. This
            // test used to name two ids literally and silently dropped the rest —
            // the sell lanes were RUNNING on the server and absent from the UI.
            // CANON_MAI is now the canonical fleet (RULE #29), so every declared
            // bot is adopted and none of them depends on being remembered here.
            const have = next.find(d => d.id === id);
            if ((!id.startsWith('mai_') || CANON_MAI.has(id)) && !have) { changed = true; next.push(mapServerBot(b)); }
            // THE SERVER OWNS status FOR THE CANONICAL FLEET (God 2026-09-09).
            // Adoption used to be add-if-missing only, so a bot already present
            // from this device's localStorage kept whatever status was persisted
            // there — and KILL ALL writes STOPPED for every bot (line ~1725).
            // Once that happened the card stayed "not running" for ever against a
            // server that said RUNNING, which is exactly the 6-vs-7 God saw: all
            // seven cards render from laneDeployments, while runningDeps counts
            // only status === 'RUNNING'. Measured 2026-09-09: Mongo and
            // bot_registry_dynamic BOTH reported 7 RUNNING and no deployment
            // carried pendingApproval, so the missing one could only be stale
            // local state. Deployment status is server truth (RULE #29); the
            // per-device cache may not outvote it.
            else if (have && CANON_MAI.has(id)) {
              const srvStatus = (mapServerBot(b) as any).status;
              if (srvStatus && (have as any).status !== srvStatus) {
                (have as any).status = srvStatus;
                changed = true;
              }
            }
          }
          return changed ? next : p;
        });
      }

      // Per-bot ledger stats for the deployed-strategy cards (current mode).
      const bj = await safeFetch(`${API_BASE_URL}/api/trades/bot-stats?mode=${tradingMode}&scope=active`, 6000);   // ACTIVE Pine only (God 2026-10-12)
      if (bj) {
        const map: Record<string, any> = {};
        (Array.isArray(bj?.bots) ? bj.bots : []).forEach((b: any) => { if (b.botId) map[b.botId] = b; });
        setBotStats(map);
        setBotStatsLoaded(true);
      }
      // ...and the SAME endpoint windowed to today, so the card's headline number
      // and the header's headline number are the same measurement of the same day.
      // SESSION day, bounded on both ends, so the card's headline is the same
      // measurement as the header's (and never a half-open "since midnight").
      const _ist = sessionIso();
      const btj = await safeFetch(`${API_BASE_URL}/api/trades/bot-stats?mode=${tradingMode}&from=${_ist}&to=${_ist}`, 6000);
      if (btj) {
        const tmap: Record<string, any> = {};
        (Array.isArray(btj?.bots) ? btj.bots : []).forEach((b: any) => { if (b.botId) tmap[b.botId] = b; });
        setBotStatsToday(tmap);
      }

      // Phase 6 — signal accounting (EXECUTED/SKIPPED + reasons) for the RUDRA panel.
      const sj = await safeFetch(`${API_BASE_URL}/signals/log?date=${sessionIso()}`, 5000);
      if (sj) {
        if (sj?.counts) setSigCounts(sj.counts);
        if (Array.isArray(sj?.log)) setSigLog(sj.log);
      }

      // Live order-book pressure per bot (the option each bot currently holds).
      const dj = await safeFetch(`${API_BASE_URL}/api/depth/intel`, 5000);
      if (dj) {
        const dmap: Record<string, any> = {};
        (Array.isArray(dj?.positions) ? dj.positions : []).forEach((p: any) => {
          if (p.bot_id && p.depth) dmap[p.bot_id] = { bias: p.bias, buyPct: p.depth.buy_pct, option_symbol: p.option_symbol, security_id: p.security_id || 0 };
        });
        setBotDepth(dmap);
      }
    } finally {
      syncInFlight.current = false;   // release the in-flight guard for the next cycle
    }
  }, [backendConnected, addLog, enableNotifications, tradingMode]);

  useEffect(() => {
    fetchBackendData();
    // 🔁 hidden = 5× slow (3s → 15s) — poora pause NAHI: positions/PnL sync
    // aur trade NOTIFICATIONS background tab me bhi aate rehne chahiye.
    let n = 0;
    const id = setInterval(() => {
      n++;
      if (document.hidden && n % 5 !== 0) return;
      fetchBackendData();
    }, BACKEND_SYNC_INTERVAL);
    // wapas aate hi turant full sync — 15s purana data na dikhe
    const onVis = () => { if (!document.hidden) { fetchBackendData(); fetchSignals(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchBackendData, fetchSignals]);

  // ── Per-position manual SQUARE-OFF — exit ONE open position on demand ──
  // Routes a SELL/"MANUAL EXIT" through the SAME webhook the bots use; the
  // backend exit path finds the open position (bot+symbol+side) and closes it
  // at the live LTP → posts CLOSE to the ledger → row moves OPEN → WIN/LOSS.
  const [sqOffBusy, setSqOffBusy] = useState('');
  // Per-position close via the LEDGER-DIRECT endpoint — resolves the live/last
  // LTP server-side and posts a matched EXIT to the canonical ledger. Robust
  // even for orphan OPEN rows (no in-memory position needed).
  const squareOffPosition = useCallback(async (t: any) => {
    const tradeId = t.id || t.tradeId;
    const optSym = t.option_symbol || t.symbol || '';
    if (!tradeId) { showToast('⚠ Trade id missing'); return; }
    // M-H2 (D25): the confirm is the HOLD on the button (ExecControl) — no window.confirm.
    const rowKey = String(tradeId);
    setSqOffBusy(rowKey);
    try {
      const r = await fetch(`${API_BASE_URL}/api/trades/square-off`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tradeId }),
      });
      const j = await r.json().catch(() => ({}));
      if (j?.status === 'success') {
        addLog('SQUARE-OFF', `✅ Squared off ${optSym}`, 'SUCCESS');
        showToast(`✅ ${optSym} squared off`);
      } else {
        addLog('SQUARE-OFF', `⚠ ${optSym}: ${j?.message || 'exit failed'}`, 'WARNING');
        showToast(`⚠ ${j?.message || 'exit failed — position held'}`);
      }
      setTimeout(fetchBackendData, 1200);
    } catch (e: any) {
      addLog('SQUARE-OFF', `❌ ${optSym}: ${e.message}`, 'ERROR');
      showToast(`❌ Square-off error`);
    } finally { setSqOffBusy(''); }
  }, [addLog, showToast, fetchBackendData]);

  // Close EVERYTHING now (manual EOD) — closes all open ledger positions.
  const [sqOffAllBusy, setSqOffAllBusy] = useState(false);
  const squareOffAllPositions = useCallback(async () => {
    // M-H2 (D25): confirmed by a hold on the button (ExecControl), not window.confirm.
    setSqOffAllBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/trades/square-off-all`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const j = await r.json().catch(() => ({}));
      addLog('SQUARE-OFF', `🔚 Square-off all → ${j?.closed ?? 0}/${j?.total ?? 0} closed`, 'WARNING');
      showToast(`✅ ${j?.closed ?? 0} positions squared off`);
      setTimeout(fetchBackendData, 1500);
    } catch (e: any) {
      addLog('SQUARE-OFF', `❌ Square-off all: ${e.message}`, 'ERROR');
    } finally { setSqOffAllBusy(false); }
  }, [backendOpenTrades.length, addLog, showToast, fetchBackendData]);

  // ✅ v9.0 — WEBSOCKET REAL-TIME BRIDGE
  useEffect(() => {
    let sock: Socket | null = null;
    try {
      // Unlimited reconnect attempts — GLOBAL_KILL events MUST reach the UI
      // for live trading safety. Previously capped at 3 attempts which meant
      // after 15 seconds of network blips, kill switches stopped being
      // delivered for the rest of the session. Exponential backoff caps at
      // 30s so we don't hammer the server when it's actually down.
      sock = socketIO('https://api.jaiswalcapital.com', {
        path: '/socket.io/',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 30000,
        reconnectionAttempts: Infinity,
        randomizationFactor: 0.5,
        timeout: 8000,
      });
      socketRef.current = sock;

      sock.on('connect', () => {
        setSocketConnected(true);
        addLog('SOCKET', '⚡ WebSocket LIVE — real-time mode active', 'SUCCESS');
      });
      sock.on('disconnect', () => {
        setSocketConnected(false);
        addLog('SOCKET', '🔌 WebSocket dropped — polling fallback', 'WARNING');
      });
      sock.on('trade_pnl_update', (data: any) => {
        setBackendSummary(data);
        if (data.kill_active) {
          setKillSwitchActive(true);
          setShowKillPanel(true);
          setMasterSwitch(false);
          setDeployments(p => p.map(d => ({ ...d, status: 'STOPPED' as const })));
          addLog('KILL_SWITCH', '🚨 Kill Switch activated by server!', 'ERROR');
          showToast('🚨 KILL SWITCH ACTIVE — All bots stopped!');
        }
      });
      sock.on('new_trade_executed', () => fetchBackendData());
      // 🤖 Real-time bot sync: a bot created on ANY device (or the canonical 7
      // seeded by the server) appears here instantly — no refresh (TASK 3+4).
      sock.on('bot_created', (bot: any) => {
        if (!bot || !bot.id) return;
        setDeployments(prev => {
          if (prev.some(d => d.id === bot.id)) return prev;   // dedupe (we made it)
          const dep = {
            ...(bot as Deployment),
            status: bot.status || 'STOPPED',
            pnl: bot.pnl || 0, trades: bot.trades || 0, wins: bot.wins || 0, losses: bot.losses || 0,
            lastSignal: bot.lastSignal || '—', startedAt: bot.startedAt || '—',
            webhookEnabled: true,
          } as Deployment;
          return [dep, ...prev];
        });
        addLog('SOCKET', `🤖 New bot synced: ${bot.name || bot.id}`, 'SUCCESS');
      });
      sock.on('trade_closed', (data: any) => {
        addLog('SOCKET', `📊 CLOSED: ${data.trade?.option_symbol} PnL ₹${(data.trade?.pnl||0).toFixed(2)}`, 'TRADE');
        fetchBackendData();
      });
      sock.on('GLOBAL_KILL_SWITCH', (data: any) => {
        setKillSwitchActive(true);
        setShowKillPanel(true);
        setMasterSwitch(false);
        setDeployments(p => p.map(d => ({ ...d, status: 'STOPPED' as const })));
        addLog('KILL_SWITCH', `🚨 ${data.reason} | PnL: ₹${data.todayPnL}`, 'ERROR');
        showToast(`🚨 KILL SWITCH: ${data.reason}`);
      });
      sock.on('KILL_SWITCH_RESET', () => {
        setKillSwitchActive(false);
        setShowKillPanel(false);
        addLog('KILL_SWITCH', '🟢 Kill Switch reset. New trading day.', 'SUCCESS');
        showToast('🟢 Kill Switch Reset — Trading resumed!');
      });
      sock.on('expiry_rolled', (data: any) => {
        setCurrentExpiry(data.expiry);
        setExpiryRolled(true);
        addLog('EXPIRY', `🔄 Expiry auto-rolled → ${data.expiry}`, 'WARNING');
        showToast(`🔄 Expiry Rolled → ${data.expiry}`);
        setTimeout(() => setExpiryRolled(false), 10000);
      });
    } catch (err: any) {
      addLog('SOCKET', `⚠️ WebSocket init failed: ${err.message}`, 'WARNING');
    }
    return () => { try { if (sock) sock.disconnect(); } catch {} };
  }, [addLog, fetchBackendData]);

  // ✅ v9.0 — DASHBOARD STATS BROADCAST
  useEffect(() => {
    const broadcast = () => {
      try {
        const stats = {
          running_bots: deployments.filter(d => d.status === 'RUNNING').length,
          paper_pnl:    paperTrades.reduce((a, t) => a + t.pnl, 0),
          live_pnl:     liveTrades.reduce((a, t) => a + t.pnl, 0),
          paper_trades: paperTrades.length,
          live_trades:  liveTrades.length,
          trading_mode: tradingMode,
          master_armed: masterSwitch,
          kill_active:  killSwitchActive,
          last_updated: new Date().toISOString(),
        };
        localStorage.setItem('jc_live_stats_bridge', JSON.stringify(stats));
        window.dispatchEvent(new CustomEvent('JC_STATS_UPDATE', { detail: stats }));
      } catch {}
    };
    broadcast();
    // Pausable mirror — hide par pause, show par interval RE-CREATE hota hai.
    // (Pehle show par sirf ek broadcast hota tha, interval kabhi wapas nahi
    // banta — quiet period me dashboard mirror hamesha ke liye freeze.)
    let id: ReturnType<typeof setInterval> | null = setInterval(broadcast, 5000);
    const onVis = () => {
      if (document.hidden) { if (id) { clearInterval(id); id = null; } }
      else if (!id) { broadcast(); id = setInterval(broadcast, 5000); }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { if (id) clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [deployments, paperTrades, liveTrades, tradingMode, masterSwitch, killSwitchActive]);

  // 🆕 v6.0 — SMART SIGNAL SENDER WITH AUTO-PRICER
  //  Pipeline: TradingView signal → backend resolves spot+expiry+strike+real LTP → fires order
  const sendSignalToBackend = useCallback(async (signalData: any) => {
    const { bot_id, auth_token, action, symbol, price, mode, option_type, strike_type, quantity, comment, segment, src } = signalData;
    const bot = deployments.find(d => d.id === bot_id);

    if (!bot) { addLog('WEBHOOK', `❌ Unknown bot_id: ${bot_id}`, 'ERROR'); return; }
    if (bot.status !== 'RUNNING') {
      addLog('WEBHOOK', `⏸️ ${bot.name} not running — signal dropped`, 'WARNING'); return;
    }
    if (!bot.autoFireEnabled) {
      addLog('WEBHOOK', `🛑 Auto-Fire OFF for ${bot.name} — signal logged only`, 'WARNING'); return;
    }

    const optType = option_type || bot.assets[0]?.optType || 'CE';
    const strikeMode = strike_type || bot.assets[0]?.strikeMode || 'ATM';
    const autoComment = comment || (
      action?.toLowerCase() === 'sell' ? '13 EMA Exit' : `BUY ${optType}`
    );

    addLog('TV_SIGNAL', `📡 ${action?.toUpperCase()} ${optType}@${strikeMode} [${autoComment}] ${symbol} → ${bot.name}`, 'TRADE');

    try {
      const res = await fetch(WEBHOOK_BASE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bot_id,
          auth_token: tvToken || auth_token || bot.apiKey,
          action,
          comment: autoComment,
          symbol,
          price,
          mode: mode || bot.tradingMode.toLowerCase(),
          option_type: optType,
          strike_type: strikeMode,
          // Per-symbol routing (Options/Futures/Cash/Commodity/ETF) → backend
          // routes accordingly. Only an EXPLICIT trade-mode is forwarded; the
          // asset's exchange segment ('NSE F&O') is NOT sent, so the backend's
          // safe index→option default still applies when unset.
          segment: (segment && /option|future|cash|equity|commodity|etf/i.test(segment)) ? segment : '',
          quantity: quantity || bot.assets[0]?.qty || getLotSize(symbol),
          // 🆕 v6.0 — Backend will auto-resolve real CE/PE LTP from option chain
          auto_resolve_ltp: true,
          // ORIGIN TAG (God 2026-09-04, Phase 7). Every signal says where it
          // came from, and a mobile-originated order is separable in the ledger
          // forever. Passed EXPLICITLY by the caller — never inferred from the
          // viewport, because a desktop browser dragged narrow is still a
          // desktop, and an audit trail that guesses is not an audit trail.
          src: src || 'desktop',
        })
      });
      const result = await res.json();
      if (result.status === 'success') {
        // PAPER mode may return only a logged signal (no full trade object) —
        // guard so an undefined result.trade never crashes the handler.
        const t = result.trade || {};
        const sym2 = t.option_symbol || result.option_symbol || symbol;
        const qty2 = t.quantity || quantity || bot.assets[0]?.qty || getLotSize(symbol);
        const ltpDisplay = t.entry_price ? `₹${Number(t.entry_price).toFixed(2)}` : (result.message || 'logged');
        addLog('LIVE_EXEC', `⚡ ${action?.toUpperCase()} ${sym2} × ${qty2} @ ${ltpDisplay}`, 'TRADE');
        showToast(`🟢 ${action?.toUpperCase()} ${sym2} @ ${ltpDisplay}`);
        if (enableNotifications) {
          notify(`⚡ Signal Fired: ${action?.toUpperCase()} ${optType}`, `${sym2} × ${qty2} @ ${ltpDisplay}`, 'success');
        }
        fetchBackendData();
      } else if (result.status === 'duplicate') {
        // Already fired for this bar (direct webhook / H1+H2 double-send) — NOT an
        // error. Logging it as info stops multi-bot setups looking "rejected".
        addLog('LIVE_EXEC', `↩️ Duplicate ignored — already fired for this bar`, 'INFO');
      } else {
        // Show the REAL reason. Backend now sends a readable `message`; older builds
        // only set `order` (BOT_INACTIVE / BOT_STOPPED / DEPLOY_TARGET_MISMATCH /
        // *_LIMIT_HIT …) — that hidden code was the old "unknown error". A gate
        // 'rejected' is a decision (warning); only true errors stay red.
        const reason = result.message || result.order || result.status || 'unknown error';
        const isReject = result.status === 'rejected';
        addLog('LIVE_EXEC', `${isReject ? '⏸️ Signal skipped' : '❌ Backend rejected'}: ${reason}`, isReject ? 'WARNING' : 'ERROR');
        if (enableNotifications) notify(isReject ? '⏸️ Signal Skipped' : '❌ Signal Rejected', reason, 'error');
      }
    } catch (e: any) {
      addLog('LIVE_EXEC', `❌ Network error: ${e.message}`, 'ERROR');
    }
  }, [deployments, addLog, fetchBackendData, getLotSize, enableNotifications]);

  // 🔥 Keep the WS auto-fire handler pointing at the CURRENT state. A signal is
  // fired once (deduped by id) only when the global switch is on AND the target
  // bot is RUNNING with Auto-Fire enabled — otherwise it's display-only.
  const firedSignalsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    autoFireRef.current = (s: any) => {
      if (!masterSwitch || !s?.action || !s?.bot_id) return;
      const bot = deployments.find(d => d.id === s.bot_id);
      if (!bot || bot.status !== 'RUNNING' || !bot.autoFireEnabled) return;
      // 🕐 TRADE-WINDOW guard (defense-in-depth — primary gate backend par hai,
      // par uska registration POST best-effort tha; ab client bhi enforce karta
      // hai). IST epoch se derive — browser TZ se independent.
      const wins = (bot.tradeWindows || []).filter((w: any) => w && w.enabled !== false && w.start && w.end);
      if (wins.length) {
        const istMins = (() => { const d = new Date(Date.now() + 5.5 * 3600 * 1000); return d.getUTCHours() * 60 + d.getUTCMinutes(); })();
        const toMin = (t: string) => { const [h, m] = String(t).split(':').map(Number); return (h || 0) * 60 + (m || 0); };
        if (!wins.some((w: any) => istMins >= toMin(w.start) && istMins <= toMin(w.end))) {
          addLog(bot.model, `⏳ ${bot.name}: signal trade-window ke BAHAR — entry skip (exits backend par kabhi block nahi hote)`, 'WARNING');
          return;
        }
      }
      // Dedup key: `s.time || ''` collapse bug fixed — time absent ho to id,
      // warna minute-bucket (same-minute retransmit dedup hota hai, agli
      // minute ki legitimate re-entry fire hoti hai; pehle poore session ki
      // re-entries chupchaap swallow ho jaati thin).
      const sigId = `${s.time || s.id || `m${Math.floor(Date.now() / 60000)}`}_${s.symbol}_${s.action}_${s.bot_id}`;
      if (firedSignalsRef.current.has(sigId)) return;     // exactly-once per signal
      firedSignalsRef.current.add(sigId);
      // 800 par full-wipe abhi-add hui key bhi kha jaata tha (boundary par
      // double-fire window) — ab reset current key ke saath hota hai.
      if (firedSignalsRef.current.size > 800) firedSignalsRef.current = new Set([sigId]);
      sendSignalToBackend({
        bot_id: s.bot_id,
        auth_token: tvToken || bot.apiKey,
        action: s.action,
        comment: s.comment,
        symbol: s.symbol,
        price: s.spot || s.price || 0,
        option_type: s.opt_type,
        strike_type: s.strike_type,
        quantity: s.quantity,
        mode: bot.tradingMode.toLowerCase(),
      });
    };
  }, [deployments, masterSwitch, tvToken, sendSignalToBackend]);

  // Styled LIVE-activation modal — replaces the native `window.confirm()` that
  // routed real-money toggles through a default OS dialog (one mis-tap → live
  // trading on real capital).
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);
  const [liveConfirmText, setLiveConfirmText] = useState('');
  // DUAL-MODE: PAPER and LIVE bots run SIDE-BY-SIDE. The header toggle is only
  // the dashboard VIEW (which ledger/stats show) — it never flips bot modes.
  // Each bot carries its own tradingMode, picked in the deploy/edit modal.
  // liveConfirmFor routes the typed "GO LIVE" confirm: GLOBAL = header view
  // switch, BOT = the per-bot selector, number = asset index for the
  // per-SYMBOL mode dropdown in the basket.
  const [liveConfirmFor, setLiveConfirmFor] = useState<'GLOBAL'|'BOT'|number>('GLOBAL');

  const handleModeSwitch = useCallback((mode: 'PAPER'|'LIVE') => {
    // On KALI the view is not a choice. This page exists to show the real-money
    // lane; letting it display paper figures under a red header would be the
    // most dangerous possible ambiguity.
    if (IS_KALI) {
      showToast('KALI is the LIVE lane - view cannot be switched here. Paper lives on PAPER AUTO TRADING DUMABATI.');
      return;
    }
    if (mode === tradingMode) return;
    if (mode === 'LIVE') {
      setLiveConfirmFor('GLOBAL');
      setLiveConfirmText('');
      setShowLiveConfirm(true);
      return;
    }
    // After the LIVE branch returns, mode is narrowed to 'PAPER'.
    setTradingMode('PAPER');
    addLog('SYSTEM', '🔄 Dashboard view → PAPER (har bot apne mode par chalta rahega)', 'INFO');
    showToast('📘 PAPER VIEW — bots apne-apne mode par');
    // Dual-mode: NO mass-flip of deployments here anymore.
  }, [tradingMode, addLog]);

  const confirmLiveSwitch = useCallback(() => {
    if (liveConfirmText.trim().toUpperCase() !== 'GO LIVE') return;
    setShowLiveConfirm(false);
    setLiveConfirmText('');
    if (typeof liveConfirmFor === 'number') {
      // Arms only ONE symbol of the basket; applied on Save/Deploy.
      const idx = liveConfirmFor;
      setNewDep(p => ({ ...p, assets: (p.assets || []).map((x, i2) => i2 === idx ? { ...x, tradingMode: 'LIVE' as const } : x) }));
      addLog('SYSTEM', '🔴 Symbol mode → LIVE set (Save/Deploy par apply hoga)', 'WARNING');
      showToast('🚨 Sirf is SYMBOL ka mode LIVE — ab Save/Deploy dabao');
      return;
    }
    if (liveConfirmFor === 'BOT') {
      // Arms only THIS bot (deploy/edit modal); applied on Save/Deploy.
      setNewDep(p => ({ ...p, tradingMode: 'LIVE' }));
      addLog('SYSTEM', '🔴 Bot mode → LIVE set (Save/Deploy par apply hoga)', 'WARNING');
      showToast('🚨 Is bot ka mode LIVE — ab Save/Deploy dabao');
      return;
    }
    setTradingMode('LIVE');
    addLog('SYSTEM', '🔄 Dashboard view → LIVE (har bot apne mode par)', 'WARNING');
    showToast('🚨 LIVE VIEW — sirf LIVE bots ka ledger/stats');
    // Dual-mode: NO mass-flip of deployments here anymore.
  }, [liveConfirmText, liveConfirmFor, addLog]);

  const toggleMaster = () => {
    const nxt = !masterSwitch;
    setMasterSwitch(nxt);
    if (!nxt) {
      // LOCK = har RUNNING bot ka STOPPED status BACKEND tak (pehle sirf local
      // flip tha — server running:true rakhta tha aur ~3s me reconciler cards
      // ko wapas RUNNING kar deta tha jabki engine chalta rehta tha).
      deployments.filter(d => d.status === 'RUNNING').forEach(d => persistBotStatus(d.id, 'STOPPED'));
      setDeployments(p => p.map(d => ({...d, status:'STOPPED' as const})));
      addLog('MASTER','🔴 Global Engine LOCKED — sab bots ka STOP backend ko bheja','ERROR');
    } else {
      addLog('MASTER', `🟢 Global Engine ARMED [${tradingMode}]`, 'SUCCESS');
    }
    showToast(nxt ? '🟢 Engine Armed!' : '🔴 Engine Locked — STOP sab bots ko bheja');
  };

  // Persist a bot's RUNNING/STOPPED status to the backend so the ENGINE GATE
  // (bot_registry_dynamic.json running:flag) honours it. Without this, STOP only
  // changed local React state → the bot kept auto-firing. Socket → REST fallback.
  const persistBotStatus = (id: string, status: 'RUNNING'|'STOPPED', onSettled?: (ok: boolean) => void) => {
    // Send the FULL card, not just {id,status}: after a server-side purge the
    // record is UPSERTED on first START — a bare status-only upsert would carry
    // NO assets/timeframe, so the engine would fall back to its hardcoded
    // single symbol instead of the bot's configured basket.
    const d = deployments.find(x => x.id === id);
    const upd: any = d ? {
      id, status, name: d.name, model: d.model, assets: d.assets,
      tradingMode: d.tradingMode, side: d.side, orderType: d.orderType, product: d.product,
      targetClients: d.targetClients, targetBrokers: d.targetBrokers,
      maxLossPerDay: d.maxLossPerDay, autoFireEnabled: d.autoFireEnabled,
      tradeWindows: d.tradeWindows || [], deployTarget: d.deployTarget || 'BOTH',
      webhookUrl: d.webhookUrl, apiKey: d.apiKey,
    } : { id, status };
    sendBotUpdate(upd, `${d?.name || id} → ${status}`, onSettled);
  };

  // VERIFIED delivery for bot updates: socket emit ka ack 2.5s me na aaye (ya
  // socket down ho) to REST fallback + res.ok check. Pehle emit fire-and-forget
  // tha — dropped STOP par bot chupchaap firing karta rehta tha aur UI ~3s me
  // wapas RUNNING flip ho jaata tha, koi error nahi.
  const sendBotUpdate = (upd: any, label: string, onSettled?: (ok: boolean) => void) => {
    // K1 (2026-10-03): the caller learns when the SERVER has the change, so the
    // card can say ARMING... until then instead of claiming RUNNING up front.
    let settled = false;
    const settle = (ok: boolean) => { if (settled) return; settled = true; try { onSettled?.(ok); } catch {} };
    const viaRest = () => {
      fetch(`${API_BASE_URL}/api/bots/update`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(upd) })
        .then(async r => {
          if (!r.ok) {
            addLog('SYNC', `❌ ${label}: backend persist FAILED (HTTP ${r.status}) — server purana config/status maan raha hai!`, 'ERROR');
            showToast(`❌ ${label}: server par save NAHI hua!`);
          } else {
            // KALI Phase 1 H6: the server says when part of the edit (routing) was not stored
            try { const j = await r.clone().json(); if (j && j.warning) { addLog('SYNC', `⚠ ${label}: ${j.warning}`, 'WARNING'); showToast(`⚠ ${label}: routing save nahi hua — assignments se hota hai`); } } catch { /* no body */ }
          }
          settle(r.ok);
        })
        .catch(e => {
          addLog('SYNC', `❌ ${label}: backend persist FAILED (${e?.message || 'network'})`, 'ERROR');
          showToast(`❌ ${label}: server par save NAHI hua!`);
          settle(false);
        });
    };
    const sk = socketRef.current;
    if (sk && sk.connected) {
      let acked = false;
      sk.emit('update_bot', upd, (r: any) => {
        acked = true;
        // The server acks failures too (status 'error' / ok:false) -- only a clean ack is success.
        const bad = r && (r.status === 'error' || r.ok === false || r.error);
        if (bad) {
          addLog('SYNC', `❌ ${label}: server refused (${r.message || r.error || 'error'})`, 'ERROR');
          showToast(`❌ ${label}: server par save NAHI hua!`);
        } else if (r && r.warning) {
          addLog('SYNC', `⚠ ${label}: ${r.warning}`, 'WARNING');
          showToast(`⚠ ${label}: routing save nahi hua — assignments se hota hai`);
        }
        settle(!bad);
      });
      // Server ka handler ack na kare (ya emit gir jaaye) to REST — update
      // idempotent upsert hai, double delivery harmless.
      setTimeout(() => { if (!acked) viaRest(); }, 2500);
    } else viaRest();
  };

  const toggleBusyRef = useRef<Record<string, number>>({});
  const toggleDep = (id: string) => {
    // STOP hamesha allowed — LOCKED master ke saath bhi (warna live bot ko
    // rokne ka ekmatra rasta KILL panel reh jaata tha). Sirf START ke liye
    // master ARM zaroori hai.
    const cur0 = deployments.find(d => d.id === id);
    if (!masterSwitch && cur0?.status !== 'RUNNING') { showToast('❌ Arm Master Engine first!'); return; }
    // Oscillation guard: one bot can toggle at most once per 1.5s. Kills the
    // ARMED/STOPPED flicker regardless of what re-triggers it (stray event,
    // cross-tab echo, double render) while real clicks still work.
    const last = toggleBusyRef.current[id] || 0;
    if (Date.now() - last < 1500) return;
    toggleBusyRef.current[id] = Date.now();
    const cur = deployments.find(d => d.id === id);
    if (!cur) return;
    const newStatus: 'RUNNING'|'STOPPED' = cur.status === 'RUNNING' ? 'STOPPED' : 'RUNNING';

    // ── ALL side-effects run ONCE here (NOT inside the setState updater — React
    //    may invoke that updater multiple times, which caused the ARMED/STOPPED
    //    flicker spam). The updater below is now pure. ──
    const prevStatus = cur.status;
    persistBotStatus(id, newStatus, (ok) => {
      // Server answered: drop the pending mark; on failure put the card back to
      // what the server still believes. Either way re-read the server at once.
      setDeployments(p => p.map(d => d.id !== id ? d : (
        ok ? { ...d, armSync: undefined } as any : { ...d, status: prevStatus, armSync: undefined } as any
      )));
      fetchBackendData();
    });   // engine gate respects STOP (running:false → no auto-fire)
    try {
      const bridge = JSON.parse(localStorage.getItem('jc_mamta_bots') || '[]');
      const updatedBridge = bridge.map((b: any) => b.name === cur.name
        ? { ...b, status: newStatus === 'RUNNING' ? 'active' : 'stopped' } : b);
      localStorage.setItem('jc_mamta_bots', JSON.stringify(updatedBridge));
    } catch(e) {}
    if (newStatus === 'STOPPED') {
      addLog(cur.model, `⏸️ STOPPED: ${cur.name} — engine will skip its signals`,'WARNING');
      window.dispatchEvent(new CustomEvent('BOSS_KILL_BOT', { detail: { name: cur.name, id: cur.id, status: 'stopped' } }));
      window.dispatchEvent(new CustomEvent('JC_DASHBOARD_UPDATE', { detail: { type: 'BOT_STOP', botName: cur.name } }));
      showToast(`⏸️ ${cur.name} STOPPED — auto-trading halted`);
    } else {
      addLog(cur.model, `🚀 ARMED: ${cur.name}`,'SUCCESS');
      window.dispatchEvent(new CustomEvent('BOSS_KILL_BOT', { detail: { name: cur.name, status: 'active' } }));
      showToast(`🚀 ${cur.name} STARTED`);
    }

    // Pure updater — only computes the next state, no side-effects.
    setDeployments(p => p.map(d => d.id !== id ? d : (
      newStatus === 'STOPPED'
        ? { ...d, status: 'STOPPED' as const, armSync: 'PENDING' } as any
        : { ...d, status: 'RUNNING' as const, armSync: 'PENDING', startedAt: new Date().toLocaleTimeString('en-IN', { hour12: false }) } as any
    )));
  };

  const deleteBot = (id: string) => {
    const bot = deployments.find(d => d.id === id);
    if (!bot) return;
        // 🛑 DIRECT MEMORY OVERRIDE (On Delete)
        try {
          const bridge = JSON.parse(localStorage.getItem('jc_mamta_bots') || '[]');
          const updatedBridge = bridge.filter((b: any) => b.name !== bot.name);
          localStorage.setItem('jc_mamta_bots', JSON.stringify(updatedBridge));
        } catch(e) {}
    // ✅ v9 FIX: Remove bot from UI (setDeployments was MISSING)
    setDeployments(prev => prev.filter(d => d.id !== id));
    // Delete on the SERVER too — localStorage-only delete left the Mongo record
    // alive (often RUNNING) and the engine kept trading a "deleted" bot.
    fetch(`${API_BASE_URL}/api/bots/${encodeURIComponent(id)}`, { method: 'DELETE' }).catch(() => {});
    addLog('SYSTEM', `🗑️ Bot deleted: ${bot.name}`, 'WARNING');
    window.dispatchEvent(new CustomEvent('JC_DASHBOARD_UPDATE', { detail: { type: 'BOT_DELETE', botName: bot.name } }));
    showToast('🗑️ Bot deleted permanently');
    setConfirmDelete(null);
  };

  const updateAssetLots = (lots: number) => {
    const sym = tempAsset.baseSymbol || tempAsset.symbol.split(' ')[0];
    const lotSize = getLotSize(sym);
    const newLots = Math.max(1, lots);
    setTempAsset(p => ({ ...p, lots: newLots, qty: newLots * lotSize, lotSize }));
  };

  const updateAssetSymbol = (sym: string) => {
    const upper = sym.toUpperCase();
    const ls = getLotSize(upper);
    setTempAsset(p => ({ ...p, symbol: upper, baseSymbol: upper, lotSize: ls, qty: p.lots * ls }));
  };

  const calcAutoExpiry = (mode: string): string => {
    if (currentExpiry && mode === 'CURRENT WEEK') {
      try {
        return new Date(currentExpiry).toLocaleDateString('en-IN',
          {day:'2-digit',month:'short',year:'2-digit'}).toUpperCase();
      } catch {}
    }
    const today = new Date();
    const d = today.getDay();
    const daysToThu = (4 - d + 7) % 7 || 7;
    const expiry = new Date(today);
    if (mode === 'CURRENT WEEK')      expiry.setDate(today.getDate() + daysToThu);
    else if (mode === 'NEXT WEEK')    expiry.setDate(today.getDate() + daysToThu + 7);
    else if (mode === 'CURRENT MONTH'){ expiry.setMonth(today.getMonth()+1, 0); while(expiry.getDay()!==4) expiry.setDate(expiry.getDate()-1); }
    else                              { expiry.setMonth(today.getMonth()+2, 0); while(expiry.getDay()!==4) expiry.setDate(expiry.getDate()-1); }
    return expiry.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'2-digit'}).toUpperCase();
  };

  const addAssetToBasket = () => {
    if (!tempAsset.symbol.trim()) { showToast('❌ Symbol required!'); return; }
    let sym = tempAsset.symbol.toUpperCase();
    const baseSym = sym.split(' ')[0];
    const finalAsset: TargetAsset = { ...tempAsset, symbol: sym, baseSymbol: baseSym };

    if (['OPTION','FUTURE','COMMODITY'].includes(tempAsset.type) && tempAsset.expiry)
      sym += ` [${tempAsset.expiry}]`;
    if (tempAsset.type === 'OPTION') {
      let finalStrike = tempAsset.strike;
      if (tempAsset.autoSelected && tempAsset.strikeMode && tempAsset.strikeMode !== 'MANUAL') {
        const auto = calcAutoStrike(baseSym, tempAsset.strikeMode);
        finalStrike = auto > 0 ? `${tempAsset.strikeMode}(${auto})` : tempAsset.strikeMode;
      }
      if (!finalStrike) { showToast('❌ Strike required!'); return; }
      sym += ` ${finalStrike} ${tempAsset.optType}`;
      finalAsset.strike = finalStrike;
    }
    sym += ` (Qty:${tempAsset.qty})`;
    finalAsset.symbol = sym;

    // Instrument-level dedupe. The SAME underlying with a DIFFERENT strike /
    // CE-PE / expiry is a DIFFERENT leg and MUST be allowed — CE+PE straddles,
    // multi-strike and multi-expiry baskets. (Was base+type only → adding NIFTY
    // ATM PE after NIFTY ATM CE was blocked, so the basket stuck at 1.)
    const legSig = (a: TargetAsset) => [
      String(a.baseSymbol || a.symbol || '').toUpperCase().split(' ')[0],
      a.type,
      a.type === 'OPTION' ? (a.optType || '') : '',
      a.type === 'OPTION' ? (a.strikeMode || '') : '',
      a.type === 'OPTION' && a.strikeMode === 'MANUAL' ? String(a.strike || '') : '',
      ['OPTION', 'FUTURE', 'COMMODITY'].includes(a.type) ? (a.expiry || '') : '',
      a.timeframe || '',   // MULTI-TF: same leg alag TF par allow (God 2026-07-15)
    ].join('|').toUpperCase();
    const newSig = legSig(finalAsset);
    if ((newDep.assets || []).some(a => legSig(a) === newSig)) {
      showToast('⚠️ Yeh exact leg pehle se basket me hai'); return;
    }

    setNewDep(p => ({ ...p, assets: [...(p.assets || []), finalAsset] }));
    setTempAsset(emptyAsset());
    showToast('✅ Asset added!');
    addLog('BASKET', `+ ${sym} · ${finalAsset.lotSize} × ${finalAsset.lots} = ${finalAsset.qty}`, 'SUCCESS');

    autoResolveLotSize(baseSym);
  };

  // AUTO lot-size from the instrument master — overrides the manual guess and
  // recomputes qty = lots × lotSize. Unknown symbol → keep manual + warn.
  const autoResolveLotSize = (baseSym: string) => {
    fetch(`${API_BASE_URL}/api/instruments/meta?symbol=${encodeURIComponent(baseSym)}`)
      .then(r => r.json())
      .then(d => {
        const ls = Number(d?.lotSize) || 0;
        if (!ls) { addLog('BASKET', `⚠ ${baseSym}: lot size not in instrument master — needs review`, 'WARNING'); return; }
        setNewDep(p => ({
          ...p,
          assets: (p.assets || []).map(a =>
            (a.baseSymbol || a.symbol) === baseSym && a.lotSize !== ls
              ? { ...a, lotSize: ls, qty: (Number(a.lots) || 1) * ls }
              : a),
        }));
      })
      .catch(() => {});
  };

  // One-click Quick-Add — same synced basket the Bot Configuration modal uses.
  // Respects the lots the user picked in the builder stepper (was hardcoded
  // lots:1 via emptyAsset — user set 5 lots, bot traded 1 lot all day).
  const quickAddAsset = (sym: string, kind: 'INDEX'|'STOCK'|'ETF'|'COMMODITY') => {
    const baseSym = sym.toUpperCase();
    const type = kind === 'INDEX' ? 'OPTION' : kind === 'STOCK' ? 'STOCK' : kind === 'ETF' ? 'ETF' : 'COMMODITY';
    // MULTI-TF (God 2026-07-15): same index ko dobara add karne par block nahi —
    // agla scalp-TF (5s → 1s → 15s → 1m) ki nayi leg banti hai, taaki ek index
    // ka 1s AUR 5s dono ek saath run/chart ho. Saare scalp-TF bhar jaayein to hi
    // block. Pehli leg BOT-inherit (blank) rehti hai — legacy behaviour intact.
    const SCALP_TFS = ['5s', '1s', '15s', '1m'];
    // God 2026-07-16 dup-fix: INDICES_LIST 'NIFTY' bhejta hai par hydrated legs
    // 'NIFTY_50' hote hain — canonical match na ho to `existing` khali aata tha
    // aur ek PARALLEL 'NIFTY' leg ban jati thi (chart me NIFTY 2 chips + TF split).
    // Ab canonical se match; nayi multi-TF leg EXISTING leg ki hi spelling reuse
    // karti hai (ek hi symbol, do TF).
    const _canon = (x: string) => String(x || '').toUpperCase().replace(/\s+/g, '_')
      .replace(/^NIFTY(50)?$/, 'NIFTY_50').replace(/^BANKNIFTY$/, 'BANK_NIFTY').replace(/^FINNIFTY$/, 'FIN_NIFTY');
    const existing = (newDep.assets || []).filter(a => a.type === type && _canon(a.baseSymbol || a.symbol) === _canon(baseSym));
    const lots = Math.max(1, Number(tempAsset.lots) || 1);
    const ls = getLotSize(baseSym);
    if (existing.length) {
      const used = new Set(existing.map(a => a.timeframe || ''));
      const nextTf = SCALP_TFS.find(t => !used.has(t));
      if (!nextTf) { showToast('⚠️ Is index ke saare scalp-TF (5s/1s/15s/1m) basket me pehle se hain'); return; }
      const baseFor = existing[0].baseSymbol || existing[0].symbol || baseSym;   // existing leg ki spelling reuse
      const asset: TargetAsset = { ...emptyAsset(), symbol: baseFor, baseSymbol: baseFor,
        type: type as TargetAsset['type'], lots, lotSize: existing[0].lotSize || ls, qty: lots * (existing[0].lotSize || ls), timeframe: nextTf };
      setNewDep(p => ({ ...p, assets: [...(p.assets || []), asset] }));
      autoResolveLotSize(baseFor);
      showToast(`✅ ${baseFor.replace(/_/g, ' ')} @ ${nextTf} add — multi-TF (row ka TF badal sakte ho)`);
      return;
    }
    const asset: TargetAsset = { ...emptyAsset(), symbol: baseSym, baseSymbol: baseSym,
      type: type as TargetAsset['type'], lots, lotSize: ls, qty: lots * ls };
    setNewDep(p => ({ ...p, assets: [...(p.assets || []), asset] }));
    autoResolveLotSize(baseSym);
  };

  // Edit lots of an asset ALREADY in the basket — pehle add ke baad lots change
  // karne ka koi control hi nahi tha (basket value read-only thi).
  const updateBasketLots = (i: number, lots: number) => {
    const n = Math.max(1, Math.floor(Number(lots) || 1));
    setNewDep(p => ({
      ...p,
      assets: (p.assets || []).map((a, idx) =>
        idx === i ? { ...a, lots: n, qty: n * (Number(a.lotSize) || 0) || a.qty } : a),
    }));
  };

  // SYNC: re-hydrate the basket from the unified backend record so this modal and
  // the Bot Configuration modal always show the SAME list (single source of truth).
  const hydrateEditAssets = (botId: string) => {
    fetch(`${API_BASE_URL}/api/bots/get?id=${encodeURIComponent(botId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const assets: any[] = d?.bot?.assets;
        if (!Array.isArray(assets) || !assets.length) return;
        setNewDep(p => ({
          ...p,
          // ⏱ bot-level TF from the unified record (Bot Configuration sets it too)
          ...(d?.bot?.timeframe ? { timeframe: String(d.bot.timeframe) } : {}),
          // 🕐 STALE-OVERWRITE FIX: windows/side/product/risk pehle sirf LOCAL
          // card se aate the — doosre device/modal ka naya server config yahan
          // nahi dikhta tha aur Save purane values se use CLOBBER kar deta tha.
          // Ab server ka sach hi modal ka starting point hai.
          ...(Array.isArray(d?.bot?.tradeWindows) ? { tradeWindows: d.bot.tradeWindows } : {}),
          ...(d?.bot?.side ? { side: d.bot.side } : {}),
          ...(d?.bot?.orderType ? { orderType: d.bot.orderType } : {}),
          ...(d?.bot?.product ? { product: d.bot.product } : {}),
          ...(d?.bot?.maxLossPerDay !== undefined ? { maxLossPerDay: Number(d.bot.maxLossPerDay) } : {}),
          ...(d?.bot?.capitalAllocatedPct !== undefined ? { capitalAllocatedPct: Number(d.bot.capitalAllocatedPct) } : {}),
          // Dual-mode: bot's own PAPER/LIVE from the unified record, so the
          // modal's TRADING MODE selector shows the true server-side state.
          ...(d?.bot?.tradingMode ? { tradingMode: (String(d.bot.tradingMode).toUpperCase() === 'LIVE' ? 'LIVE' : 'PAPER') as 'PAPER'|'LIVE' } : {}),
          // 🎛 per-client routing matrix from the unified record
          ...(d?.bot?.clientRouting && typeof d.bot.clientRouting === 'object' ? { clientRouting: d.bot.clientRouting } : {}),
          assets: assets.map(a => ({
            ...emptyAsset(),
            symbol: String(a.symbol || a.baseSymbol || '').toUpperCase(),
            baseSymbol: String(a.baseSymbol || a.symbol || '').toUpperCase(),
            type: (a.type === 'OPTIONS_ATM' || /OPTION/i.test(a.type || '') ? 'OPTION'
              : /FUT/i.test(a.type || '') ? 'FUTURE'
              : /ETF/i.test(a.type || '') ? 'ETF'
              : /COMM|MCX/i.test(a.type || '') ? 'COMMODITY'
              : /CASH|EQUITY|STOCK/i.test(a.type || '') ? 'STOCK' : 'OPTION') as TargetAsset['type'],
            lots: Number(a.lots) || 1,
            lotSize: Number(a.lotSize) || 0,
            qty: (Number(a.lots) || 1) * (Number(a.lotSize) || 0) || (Number(a.qty) || 75),
            sl: Number(a.sl) || 20, target: Number(a.target) || 60,
            trailingSlPts: Number(a.trail ?? a.trailingSlPts) || 10,
            // ONE UNIFIED RECORD: keep the fields the OTHER modal (Mamta AI Bot
            // Configuration) + the basket builder own — previously this hydrate
            // DROPPED them, so saving from this modal wiped per-symbol ON/OFF
            // (all legs flipped back ON) and per-leg optType/strike/expiry.
            enabled: a.enabled !== false,
            ...(a.optType ? { optType: a.optType } : {}),
            ...(a.strikeMode ? { strikeMode: a.strikeMode } : {}),
            ...(a.strike ? { strike: a.strike } : {}),
            ...(a.expiry ? { expiry: a.expiry } : {}),
            ...(a.segment ? { segment: a.segment } : {}),
            ...(typeof a.manualRiskEnabled === 'boolean' ? { manualRiskEnabled: a.manualRiskEnabled } : {}),
            // ⏱ per-symbol TF from the unified record — DON'T drop it, warna is
            // modal ka save Bot Configuration ka set kiya TF wipe kar dega
            ...(a.timeframe ? { timeframe: String(a.timeframe) } : {}),
            // Dual-mode: per-symbol PAPER/LIVE override — same no-drop rule.
            ...(a.tradingMode && ['PAPER','LIVE'].includes(String(a.tradingMode).toUpperCase())
              ? { tradingMode: String(a.tradingMode).toUpperCase() as 'PAPER'|'LIVE' } : {}),
            // 🕔 Trishul zones + 👥 per-symbol client routing — same no-drop rule
            // (hydrate whitelist DROPPED these → edit-open + save silently wiped them).
            ...(Array.isArray(a.zones) && a.zones.length ? { zones: a.zones } : {}),
            ...(a.clientRouting && typeof a.clientRouting === 'object' && Object.keys(a.clientRouting).length
              ? { clientRouting: a.clientRouting } : {}),
          })),
        }));
        assets.forEach(a => { if (!Number(a.lotSize)) autoResolveLotSize(String(a.baseSymbol || a.symbol || '').toUpperCase()); });
      })
      .catch(() => {});
  };

  const removeAssetFromBasket = (i: number) => {
    setNewDep(p => ({ ...p, assets: (p.assets || []).filter((_,idx) => idx !== i) }));
  };

  // Push a bot's ON/OFF trade windows to the backend gate so it only fires entries
  // inside an ON window (same FastAPI route the BOTS-COMMAND tab uses). Best-effort.
  const pushWindowsToGate = (botId: string, windows?: { start: string; end: string; enabled: boolean }[]) => {
    if (!windows) return;
    // Response ab VERIFY hota hai — pehle .catch(()=>{}) tha aur UI "✅ Bot
    // updated" dikhata tha chahe gate registration fail hua ho (bot windows
    // ke bahar firing karta, user ko time-gated ka bharosa).
    fetch(`${API_BASE_URL}/api/bots/trade-windows`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bot_id: botId, windows }),
    }).then(r => {
      if (!r.ok) {
        addLog('SYNC', `❌ ${botId}: trade-windows gate registration FAILED (HTTP ${r.status}) — backend window enforce NAHI karega!`, 'ERROR');
        showToast('❌ Trade windows server par register NAHI hue — dobara save karo!');
      }
    }).catch(e => {
      addLog('SYNC', `❌ ${botId}: trade-windows gate FAILED (${e?.message || 'network'})`, 'ERROR');
      showToast('❌ Trade windows server par register NAHI hue — dobara save karo!');
    });
  };

  // 🔒 SEGMENT LOCK per Rudra model (segment rules SACRED — same as the Mamta AI
  // Bot Configuration modal): VEG = options (index/stock underlyings), SHOOL =
  // futures (index/stock), STHAMBH = cash/ETF. null = no lock (WEBHOOK/custom).
  const modelTypeLock = (model?: string): TargetAsset['type'][] | null => {
    const m = String(model || '').toUpperCase();
    return m === 'VEG' ? ['OPTION', 'INDEX'] : m === 'SHOOL' ? ['FUTURE'] : m === 'STHAMBH' ? ['STOCK', 'ETF'] : null;
  };
  const modelQuickKinds = (model?: string): string[] | null => {
    const m = String(model || '').toUpperCase();
    return m === 'VEG' || m === 'SHOOL' ? ['INDEX', 'STOCK'] : m === 'STHAMBH' ? ['STOCK', 'ETF'] : null;
  };

  const deployBot = () => {
    if (!newDep.assets?.length && newDep.model !== 'WEBHOOK') {
      showToast('❌ Add at least one asset!'); return;
    }
    if (editingBotId) {
      // Dual-mode: the bot keeps ITS OWN tradingMode (picked in the modal) —
      // never clobbered by the global dashboard view anymore.
      const botMode = (newDep.tradingMode as 'PAPER'|'LIVE') || 'PAPER';
      setDeployments(p => p.map(d => d.id === editingBotId ? { ...d, ...newDep, tradingMode: botMode } as Deployment : d));
      addLog('SYSTEM', `✏️ Bot updated: ${newDep.name} [${botMode}]`, 'SUCCESS');
      showToast('✅ Bot updated!');
      // Persist the edit to the UNIFIED backend (was local-only before) + gate windows.
      const upd = {
        id: editingBotId, name: newDep.name, tradeWindows: newDep.tradeWindows || [],
        assets: newDep.assets, side: newDep.side, orderType: newDep.orderType, product: newDep.product,
        targetClients: newDep.targetClients, targetBrokers: newDep.targetBrokers,
        maxLossPerDay: newDep.maxLossPerDay, autoFireEnabled: newDep.autoFireEnabled, tradingMode: botMode,
        deployTarget: newDep.deployTarget || 'BOTH',
        // Risk % pehle EDIT payload se GAYAB tha (create me tha) — user 20→5
        // karta to local card 5 dikhata par engine 20 par size karta rehta.
        ...(newDep.capitalAllocatedPct !== undefined ? { capitalAllocatedPct: newDep.capitalAllocatedPct } : {}),
        // ⏱ bot-level TF — same unified field the Bot Configuration modal pushes
        ...(newDep.timeframe ? { timeframe: newDep.timeframe } : {}),
        // 🎛 multi-client × multi-broker routing matrix
        ...(newDep.clientRouting ? { clientRouting: newDep.clientRouting } : {}),
      };
      sendBotUpdate(upd, `edit:${newDep.name || editingBotId}`);
      pushWindowsToGate(editingBotId, newDep.tradeWindows);
    } else {
      const botId = 'bot_' + Date.now();
      const dep: Deployment = {
        ...(newDep as Deployment), id: botId,
        name: newDep.name || `${newDep.model} — ${newDep.assets?.[0]?.symbol || 'Auto'}`,
        status:'STOPPED', pnl:0, trades:0, wins:0, losses:0,
        lastSignal:'—', startedAt:'—', webhookEnabled:true,
        webhookUrl: WEBHOOK_BASE_URL, apiKey: generateApiKey(),
        // Dual-mode: the mode chosen in the modal (default PAPER) — NOT the
        // global dashboard view. PAPER and LIVE bots coexist.
        apiSecret:'', tradingMode: (newDep.tradingMode as 'PAPER'|'LIVE') || 'PAPER',
      };
      recentCreateRef.current[dep.id] = Date.now();   // shield from existence-reconcile until server save lands
      setDeployments(p => [dep, ...p]);
      addLog('SYSTEM', `🚀 New bot deployed: ${dep.name}`, 'SUCCESS');
      showToast('✅ Bot deployed!');

      // ☁️ Persist + broadcast via the backend (single source of truth). We send
      // OUR id so the server keeps it — the echoed 'bot_created' is then deduped
      // by the listener (no duplicate card), and every other open dashboard adds
      // the card live. Socket is the reliable channel; REST is the fallback.
      const createPayload = {
        id: dep.id, name: dep.name, strategy: dep.model, model: dep.model,
        symbol: dep.assets?.[0]?.baseSymbol || dep.assets?.[0]?.symbol || '',
        assets: dep.assets, tradingMode: dep.tradingMode, status: dep.status,
        side: dep.side, orderType: dep.orderType, product: dep.product,
        targetClients: dep.targetClients, targetBrokers: dep.targetBrokers,
        autoFireEnabled: dep.autoFireEnabled, webhookUrl: dep.webhookUrl, apiKey: dep.apiKey,
        maxLossPerDay: dep.maxLossPerDay, capitalAllocatedPct: dep.capitalAllocatedPct,
        tradeWindows: dep.tradeWindows || [], deployTarget: dep.deployTarget || 'BOTH',
        ...(dep.timeframe ? { timeframe: dep.timeframe } : {}),
        ...(dep.clientRouting ? { clientRouting: dep.clientRouting } : {}),
      };
      pushWindowsToGate(dep.id, dep.tradeWindows);
      const sock = socketRef.current;
      if (sock && sock.connected) {
        sock.emit('create_bot', createPayload, (r: any) => {
          if (r?.status === 'success') addLog('SYSTEM', `☁️ Bot saved to server: ${dep.name}`, 'SUCCESS');
          else addLog('SYSTEM', `⚠️ Server save failed: ${r?.message || 'unknown'}`, 'WARNING');
        });
      } else {
        fetch(`${API_BASE_URL}/api/bots/create`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(createPayload),
        }).then(r => r.json()).then(r => {
          if (r?.status !== 'success') addLog('SYSTEM', `⚠️ Server save failed: ${r?.message || 'unknown'}`, 'WARNING');
        }).catch(() => addLog('SYSTEM', '⚠️ Server save failed (offline) — local only', 'WARNING'));
      }
    }
    setShowModal(false); setEditingBotId(null);
    setNewDep({
      name:'', assets:[], model:'VEG', side:'BOTH', orderType:'MARKET', product:'NRML',
      webhookEnabled:true, targetClients:['ALL'], targetBrokers:['ZERODHA','DHAN'],
      autoStrikeEnabled:true, autoFireEnabled:true, tradingMode:'PAPER',
      capitalAllocatedPct:20, maxLossPerDay:50000, activationTime:'09:15', autoStopTime:'15:20',
      reEntryEnabled:true, reEntryCount:3, trailingSlEnabled:true, squareOffOnTarget:true,
    });
  };

  // 🚀 ONE-CLICK DEPLOY from a RUDRA signal card — banaye ek bot pre-configured
  // us symbol + format + side ke liye, aur server pe save kar de.
  const deployFromSignal = (card: any) => {
    const fmt = String(card.format || 'VEG').toUpperCase();              // VEG/SHOOL/STHAMBH
    const baseSym = String(card.symbol || '').toUpperCase();
    if (!baseSym) { showToast('❌ Signal me symbol nahi'); return; }
    // asset type from the format/tab — VEG=OPTION · SHOOL=FUTURE · STHAMBH=CASH
    const type: TargetAsset['type'] = card.tab === 'OPTION' ? 'OPTION'
      : card.tab === 'CASH' ? (card.symbol?.endsWith('ETF') ? 'ETF' : 'STOCK')
      : fmt === 'SHOOL' ? 'FUTURE'
      : fmt === 'STHAMBH' ? (baseSym.endsWith('ETF') || baseSym.endsWith('BEES') ? 'ETF' : 'STOCK')
      : 'OPTION';
    const ls = getLotSize(baseSym);
    const asset: TargetAsset = { ...emptyAsset(), symbol: baseSym, baseSymbol: baseSym,
      type, lots: 1, lotSize: ls, qty: ls,
      ...(card.tab === 'OPTION' && card.option_strike ? { strike: String(card.option_strike) } : {}) } as TargetAsset;
    const botId = 'bot_' + Date.now();
    // STHAMBH = cash delivery, BUY only — a SELL signal means EXIT, never short
    const entrySide = fmt === 'STHAMBH' ? 'BUY' : (card.signal === 'SELL' ? 'SELL' : 'BUY');
    // Deployment.side is the bot's direction filter, not an order side — it must stay
    // inside the 'LONG ONLY'|'SHORT ONLY'|'BOTH' contract or the edit-modal dropdown
    // renders blank. STHAMBH always maps to LONG ONLY (BUY-only segment rule).
    const side: Deployment['side'] = entrySide === 'SELL' ? 'SHORT ONLY' : 'LONG ONLY';
    const dep: Deployment = {
      ...(newDep as Deployment), id: botId,
      name: `${card.format_name || fmt} — ${baseSym}${card.tab === 'OPTION' && card.option_leg ? ' ' + card.option_leg : ''}`,
      model: fmt, assets: [asset], side,
      status: 'STOPPED', pnl: 0, trades: 0, wins: 0, losses: 0,
      lastSignal: '—', startedAt: '—', webhookEnabled: true,
      webhookUrl: WEBHOOK_BASE_URL, apiKey: generateApiKey(), apiSecret: '',
      // Dual-mode: one-click deploys are ALWAYS born PAPER — going LIVE is an
      // explicit per-bot choice in the edit modal (typed GO LIVE confirm).
      tradingMode: 'PAPER', deployTarget: 'SUPERCHART', autoFireEnabled: false,
    } as Deployment;
    recentCreateRef.current[dep.id] = Date.now();   // shield from existence-reconcile until server save lands
    setDeployments(p => [dep, ...p]);
    addLog('SYSTEM', `🚀 RUDRA ${fmt} bot deployed: ${dep.name} (${entrySide} → ${side})`, 'SUCCESS');
    showToast(`✅ ${dep.name} deployed — AUTO OFF (review then turn on)`);
    const createPayload = {
      id: dep.id, name: dep.name, strategy: fmt, model: fmt, symbol: baseSym,
      assets: [asset], tradingMode: 'PAPER', status: 'STOPPED', side,
      orderType: 'MARKET', product: (card.tab === 'CASH' || fmt === 'STHAMBH') ? 'CNC' : 'NRML',
      targetClients: ['ALL'], targetBrokers: ['ZERODHA', 'DHAN'],
      autoFireEnabled: false, webhookUrl: dep.webhookUrl, apiKey: dep.apiKey,
      deployTarget: 'SUPERCHART', source: 'RUDRA_SIGNAL',
      note: card.verdict || (card.notes || []).join(' · '),
    };
    const sock = socketRef.current;
    if (sock && sock.connected) sock.emit('create_bot', createPayload, () => {});
    else fetch(`${API_BASE_URL}/api/bots/create`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(createPayload) }).catch(() => {});
  };

  const getProductLabel = (p: string) => p === 'CNC' ? 'DELIVERY' : p === 'MIS' ? 'INTRADAY' : 'F&O';

  const testWebhookCounter = useRef(0);
  const testWebhook = async (botId: string) => {
    const bot = deployments.find(d => d.id === botId);
    if (!bot) return;
    const sym = bot.assets[0]?.baseSymbol || 'NIFTY';
    // Resolve the SYMBOL's OWN live spot — NEVER a hardcoded fallback. A fixed
    // 24400 (NIFTY level) sent for BANKNIFTY (~57300) or RELIANCE (~1330) builds
    // a non-existent strike → "option LTP unresolved" → entry skipped + nothing
    // recorded. Try cached price → /atm → /option-chain spot; abort if none.
    let spot = livePrices[sym] || livePrices[sym.toUpperCase()] || 0;
    if (spot <= 0) {
      try {
        const r = await fetch(`${ATM_URL}?symbol=${encodeURIComponent(sym)}&t=${Date.now()}`, { cache: 'no-store' });
        if (r.ok) { const d = await r.json(); spot = Number(d.ltp ?? d.spot ?? 0) || 0; }
      } catch { /* fall through to chain */ }
    }
    if (spot <= 0) {
      try {
        const r = await fetch(`${OPTION_CHAIN_URL}?symbol=${encodeURIComponent(sym)}&expiry=current&t=${Date.now()}`, { cache: 'no-store' });
        if (r.ok) { const d = await r.json(); spot = Number(d.spot ?? d.underlyingValue ?? 0) || 0; }
      } catch { /* fall through to /quote */ }
    }
    if (spot <= 0) {
      // /quote has a candle-CSV last-close fallback (+ stale_ltp) → resolves even
      // when the live WS feed is briefly down (Dhan reconnecting after a restart),
      // so a feed blip no longer aborts every test/signal.
      try {
        const r = await fetch(`${API_BASE_URL}/quote?symbol=${encodeURIComponent(sym)}&t=${Date.now()}`, { cache: 'no-store' });
        if (r.ok) { const d = await r.json(); spot = Number(d.ltp ?? d.stale_ltp ?? 0) || 0; }
      } catch { /* no spot */ }
    }
    if (spot <= 0) {
      addLog('TEST', `🧪 Test aborted — ${sym} ka live spot nahi mila (market closed / feed down)`, 'WARNING');
      showToast(`⚠ ${sym}: live spot unavailable — test skip`);
      return;
    }

    const cycle = testWebhookCounter.current % 3;
    testWebhookCounter.current += 1;

    let testAction: string, testComment: string, testOptType: 'CE' | 'PE';
    if (cycle === 0) {
      testAction = 'buy'; testOptType = 'CE'; testComment = 'BUY CE';
    } else if (cycle === 1) {
      testAction = 'buy'; testOptType = 'PE'; testComment = 'BUY PE';
    } else {
      testAction = 'sell'; testOptType = 'CE'; testComment = '13 EMA Exit';
    }

    addLog('TEST', `🧪 Test #${testWebhookCounter.current}: ${testComment} → ${sym} @ ₹${spot}`, 'INFO');
    sendSignalToBackend({
      bot_id: bot.id,
      auth_token: tvToken || bot.apiKey,
      action: testAction,
      comment: testComment,
      symbol: sym,
      price: spot,
      option_type: testOptType,
      mode: bot.tradingMode.toLowerCase()
    });
  };

  // ⭐ CORE PRINCIPLE: the UI NEVER places orders — it only emits SIGNALS.
  // Manual/TEST fires now ride the SAME SIGNAL → ROUTER → BOT → ORDER pipeline
  // as TradingView alerts (gates: BOT_STOPPED, trade windows, L/P/T limits, JIT
  // depth, TRADING_MODE master kill-switch). The old /api/dhan/place-order
  // bypass is dead from the UI (backend also blocks it behind the master guard).
  const fireDirectOrder = useCallback(async (
    symbol: string,
    action: 'BUY' | 'SELL',
    qty: number,
    mode: 'PAPER' | 'LIVE',
    optType?: 'CE' | 'PE',
    _strike?: string,
    _product = 'NRML',
    botId?: string,
    src: 'desktop' | 'mobile' = 'desktop',
  ) => {
    const bot = (botId && deployments.find(d => d.id === botId))
      || deployments.find(d => d.status === 'RUNNING');
    if (!bot) {
      showToast('🛡 Orders sirf BOTS se jaate hain — pehle koi bot START karo');
      addLog('MANUAL-SIGNAL', '🛡 Dropped: no RUNNING bot to route the manual signal', 'WARNING');
      return;
    }
    addLog('MANUAL-SIGNAL', `📡 ${action} ${symbol}${optType ? ' ' + optType : ''} ×${qty} [${mode}] → ${bot.name} via router`, 'TRADE');
    sendSignalToBackend({
      bot_id: bot.id,
      auth_token: tvToken || bot.apiKey,
      action: action.toLowerCase(),
      comment: `MANUAL ${action} ${optType || ''}`.trim(),
      symbol,
      price: livePrices[symbol] || 0,
      quantity: qty,
      option_type: optType || 'CE',
      mode: mode.toLowerCase(),
      src,
    });
    showToast(`📡 Manual signal → ${bot.name} (router se execute hoga)`);
  }, [deployments, addLog, showToast, livePrices, tvToken, sendSignalToBackend]);

  // 🆕 v6.0 — Smart Pricer Pick Handler
  const handlePricerPick = (strike: number, optType: 'CE'|'PE', ltp: number) => {
    addLog('PRICER', `🎯 Picked: ${pricerSymbol} ${strike} ${optType} @ ₹${ltp.toFixed(2)} (live LTP)`, 'SUCCESS');
    setShowOptionPricer(false);
    showToast(`✅ Picked ${strike} ${optType} @ ₹${ltp.toFixed(2)}`);
    setTempAsset(p => ({
      ...p,
      symbol: pricerSymbol,
      baseSymbol: pricerSymbol,
      strike: strike.toString(),
      optType,
      strikeMode: 'MANUAL',
      autoSelected: false,
    }));
  };

  // Robust date normaliser — trades carry `dateIso` (YYYY-MM-DD) but legacy rows
  // may only have a display `date` (DD/MM/YYYY or DD-MM-YYYY). Canonicalise both
  // sides so "Today" / a picked date match regardless of source format.
  const _canonDate = (s: string): string => {
    const str = String(s || '').trim();
    if (!str) return '';
    const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const dmy = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
    return str;
  };
  const _tradeDay = (t: TradeHistory): string => _canonDate(t.dateIso || t.date);
  const _symBase  = (sym: string): string => String(sym || '').trim().split(/\s+/)[0] || '';

  // Date-Filter summary bug fix (God 2026-07-28): the bottom totals, the flat
  // table AND the grouped hierarchy ALL derive from getFilteredTrades(), so one
  // correctly-stacked filter makes every total reflect ONLY the visible rows.
  // All four filters STACK — bot ∧ date ∧ symbol ∧ timeframe.
  const getFilteredTrades = () => {
    // Lane decides the book, not the tab. On KALI the tab cannot reach paper
    // rows at all; on DUMABATI it cannot reach live rows.
    let data = laneReportTrades;
    if (botFilter !== 'ALL')    data = data.filter(t => (t.botId || 'unknown') === botFilter);
    if (symbolFilter !== 'ALL') data = data.filter(t => _symBase(t.symbol) === symbolFilter);
    if (tfFilter !== 'ALL')     data = data.filter(t => (t.timeframe || '') === tfFilter);
    if (dateFilter === 'TODAY') {
      const n = new Date();
      const today = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
      data = data.filter(t => _tradeDay(t) === today);
    } else if (dateFilter !== 'ALL') {
      const want = _canonDate(dateFilter);
      data = data.filter(t => _tradeDay(t) === want);
    }
    return data;
  };
  const filteredTrades = getFilteredTrades();

  // Fetch the grouped period report when the modal is open + a grouping is chosen.
  useEffect(() => {
    // 'grouped' (hierarchy) is built client-side from filteredTrades; only the
    // period roll-ups (day/week/…) come from the backend /report endpoint.
    if (!showHistoryModal || groupBy === 'none' || groupBy === 'grouped' || reportTab === 'OPEN') return;
    const p = new URLSearchParams({ mode: reportTab, groupBy });
    if (botFilter !== 'ALL') p.set('botId', botFilter);
    // Symbol/TF params sent so period roll-ups honour these filters too — backend
    // support for them is staged for the 15:30 queue; harmlessly ignored until then.
    if (symbolFilter !== 'ALL') p.set('symbol', symbolFilter);
    if (tfFilter !== 'ALL') p.set('timeframe', tfFilter);
    fetch(`${API_BASE_URL}/api/trades/report?${p.toString()}&t=${Date.now()}`, { cache: 'no-store' })
      .then(r => r.json())
      .then(d => { if (d?.status === 'success') { setReportGroups(d.rows || []); setReportGrand(d.grand || null); } })
      .catch(() => {});
  }, [showHistoryModal, groupBy, reportTab, botFilter, symbolFilter, tfFilter]);

  // God 2026-08-26: these three used to union paper+live, so KALI's filter
  // dropdowns listed paper symbols, paper dates and paper timeframes — offering
  // the operator a filter for rows that lane can never show. Lane's book only.
  const uniqueDates = Array.from(new Set(laneReportTrades.map(t => t.date)));
  const uniqueSymbols = Array.from(new Set(
    laneReportTrades.map(t => String(t.symbol || '').trim().split(/\s+/)[0]).filter(Boolean)
  )).sort();
  const uniqueTFs = Array.from(new Set(
    (laneReportTrades.map(t => t.timeframe).filter(Boolean)) as string[]
  )).sort();
  // Dropdown source = every DEPLOYED bot (so a brand-new 0-trade bot shows up
  // immediately) PLUS any bot_id seen in trades (covers legacy/webhook-only bots
  // not in the deployment list). This is the TASK 3 "appears in all filters" fix.
  const uniqueBots = Array.from(new Set([
    ...laneDeployments.map(d => d.id),
    ...laneReportTrades.map(t => t.botId || 'unknown'),
  ])).filter(Boolean);
  const botLabel = (b: string) => {
    if (b === 'ALL') return 'All Bots';
    // Label lookup stays on the FULL list on purpose: a lane must not show a
    // raw id just because the bot it names belongs to the other lane.
    const dep = deployments.find(d => d.id === b);
    if (dep?.name) return dep.name;   // prefer the real bot name
    return b.replace(/^bot_/, '').replace(/_/g, ' ').toUpperCase();
  };

  // ── HIERARCHICAL TRADE REPORT (God/Subhas 2026-07-23) ─────────────────────
  // Level 1 BOT (weapon: Vega/Shool/Sthambh) → L2 SYMBOL → L3 TIMEFRAME (badge)
  // → L4 CLIENT (column). Every section AND the trades inside it are sorted by
  // Net P&L, highest profit first. buildHierReport() is the single source both
  // the modal grid and the PRINT/PDF export render from, so they mirror exactly
  // (and the backend daily PDF renders the same shape).
  // God 2026-08-24: sections are now PER BOT, not per model family, and the names
  // come from the ONE registry. Before this, "1. VEGA" was four different bots
  // added together — mai_veg + mai_veg_mcx + both SELL lanes — so a Vega Max loss
  // and a Vega profit cancelled inside one header, and the label matched nothing
  // else in the system.
  const MODEL_TAG: Record<string, string> = {
    VEG: 'High-Speed Options Scalping', SHOOL: 'Futures Positional',
    STHAMBH: 'Cash / ETF Positional', OTHER: '',
  };
  const MODEL_META: Record<string, { name: string; tag: string; col: string }> =
    Object.fromEntries([
      ...FLEET_REGISTRY.map(b => [b.id, { name: b.name, tag: MODEL_TAG[b.model] || '', col: b.accent }]),
      ['OTHER', { name: 'Other', tag: '', col: '#64748b' }],
    ]);
  // Resolves a ledger row's botId to its CANONICAL bot id (handles legacy ids
  // like 'veg_mcx' still present in older rows). Anything outside the registry
  // stays 'OTHER' rather than being guessed into a fleet bucket — a substring
  // match on "VEG" is how a Shool row could land under Vega.
  const botModelOf = (botId: string): string => {
    const hit = botOf(String(botId || ''));
    return hit ? hit.id : 'OTHER';
  };
  // "BANK_NIFTY 56500 CE" → { base:'BANK_NIFTY', strike:'56500 CE' }
  const splitSymbol = (full: string): { base: string; strike: string } => {
    const parts = String(full || '').trim().split(/\s+/);
    if (parts.length <= 1) return { base: parts[0] || '—', strike: '' };
    return { base: parts[0], strike: parts.slice(1).join(' ') };
  };
  // God 2026-08-24: the leaf group is (SYMBOL × TIMEFRAME), not SYMBOL.
  // Before this, FIN_NIFTY was ONE block carrying "⏱1m ⏱1s ⏱15s ⏱5s" badges and a
  // single P&L, with 1s/1m/15s/5s trades interleaved in one table. That block was
  // four different Pine strategies added together (1s STACK200, 5s CROSS550,
  // 15s/1m TRAP-513), so a losing leg and a winning leg cancelled inside the
  // header number and no row could be checked against the leg that produced it.
  // Now each TF gets its own section with its own W/L/BE and its own P&L —
  // "FIN_NIFTY · 1s", then "FIN_NIFTY · 1m", and so on.
  // This is RULE #9's composite symbol_TF key applied to the report layer.
  // hierReport feeds BOTH the on-screen report and the PRINT/PDF export, so
  // fixing it here fixes every surface at once (God: "globally thik karo").
  const TF_ORDER = ['1s','5s','15s','30s','1m','3m','5m','15m','30m','1h','1H','4h','4H','1d','1D','1W'];
  const tfRank = (tf: string) => { const i = TF_ORDER.indexOf(String(tf)); return i < 0 ? 999 : i; };

  const buildHierReport = (trades: TradeHistory[]) => {
    const byModel = new Map<string, Map<string, TradeHistory[]>>();
    for (const t of trades) {
      const model = botModelOf(t.botId);
      const base = splitSymbol(t.symbol).base || t.symbol || '—';
      // A trade with no stamped TF is grouped under "(no TF)" rather than being
      // folded into a real leg — an unattributable trade is a defect to see.
      const tf = (t.timeframe || '').trim() || '(no TF)';
      const key = `${base}\u0000${tf}`;
      if (!byModel.has(model)) byModel.set(model, new Map());
      const symMap = byModel.get(model)!;
      if (!symMap.has(key)) symMap.set(key, []);
      symMap.get(key)!.push(t);
    }
    // Canonical weapons always present when unfiltered → an idle bot still shows
    // its "no trades" block, exactly like the approved layout.
    // All SEVEN canonical bots always present when unfiltered → an idle bot still
    // shows its "no trades" block. Was seeded with the three model families, which
    // after the per-bot switch would have left Vega Max, both SELL lanes and Shool
    // Max invisible whenever they had no trades that day.
    if (botFilter === 'ALL') for (const b of FLEET_REGISTRY) if (!byModel.has(b.id)) byModel.set(b.id, new Map());

    const bots = Array.from(byModel.entries()).map(([model, symMap]) => {
      const meta = MODEL_META[model] || MODEL_META.OTHER;
      const symbols = Array.from(symMap.values()).map(list => {
        const base = splitSymbol(list[0].symbol).base || list[0].symbol || '—';
        const tf = (list[0].timeframe || '').trim() || '(no TF)';
        return {
          symbol: base,
          tf,
          key: `${base} ${tf}`,
          tfs: [tf],                       // kept for the badge renderer (one TF now)
          clients: Array.from(new Set(list.map(t => t.client || 'All Clients'))),
          net: list.reduce((s, t) => s + (t.pnl || 0), 0),
          wins: list.filter(t => t.status === 'WIN').length,
          losses: list.filter(t => t.status === 'LOSS').length,
          be: list.filter(t => t.status === 'BREAKEVEN').length,
          trades: [...list].sort((a, b) => (b.pnl || 0) - (a.pnl || 0)),   // highest profit first
        };
      })
      // Symbol A→Z, then TF in real time order (1s → 5s → 15s → 1m → …), NOT by
      // P&L. God reads these top-to-bottom to check a leg; a stable, predictable
      // order is what makes that check reliable, and a P&L sort reshuffles the
      // page every refresh.
      .sort((a, b) => a.symbol.localeCompare(b.symbol) || tfRank(a.tf) - tfRank(b.tf));
      return {
        model, name: meta.name, tag: meta.tag, col: meta.col, symbols,
        net: symbols.reduce((s, x) => s + x.net, 0),
        wins: symbols.reduce((s, x) => s + x.wins, 0),
        losses: symbols.reduce((s, x) => s + x.losses, 0),
        be: symbols.reduce((s, x) => s + x.be, 0),
        count: symbols.reduce((s, x) => s + x.trades.length, 0),
      };
    }).sort((a, b) => b.net - a.net);   // highest-profit weapon first
    return bots;
  };
  const hierReport = buildHierReport(filteredTrades);
  // Keyed on the bot id now, not the model family.
  const idleNote = (botId: string) => {
    const m = botOf(botId)?.model;
    return m === 'STHAMBH' ? 'Awaiting 4H candle-close confirmations.'
      : m === 'SHOOL' ? 'Awaiting futures setups.'
      : 'Awaiting signals.';
  };

  // ── SMC X-RAY (God/Subhas 2026-07-23) ─────────────────────────────────────
  // Pull the 12-TF SMC vault snapshot for a trade and open the deep-analysis
  // modal. Favourability is judged RELATIVE to the trade's direction: a CE bet
  // wants bullish tailwinds, a PE bet wants bearish ones — same reading flips
  // green<->red. This is what lets Sir see WHY a trade won or lost.
  const openXray = async (t: TradeHistory) => {
    const tradeId = t.tradeId || t.id;
    setXray({ trade: t, loading: true, err: '', data: null });
    try {
      const p = new URLSearchParams({ tradeId, t: String(Date.now()) });
      const r = await fetch(`${API_BASE_URL}/api/smc_vault/analysis?${p.toString()}`, { cache: 'no-store' });
      const d = await r.json();
      setXray({ trade: t, loading: false, data: d,
        err: d?.status === 'success' ? '' : (d?.message || d?.status || 'no data') });
    } catch {
      setXray({ trade: t, loading: false, data: null, err: 'network error — vault unreachable' });
    }
  };
  // trade direction: +1 bullish (CE/LONG fut-cash), -1 bearish (PE/SHORT), 0 = unknown
  const xrayDir = (data: any, t: TradeHistory): number => {
    const ot = String(data?.trade?.optType || data?.entry?.option_type || '').toUpperCase();
    if (ot === 'CE') return 1;
    if (ot === 'PE') return -1;
    const os = String(data?.trade?.optionSymbol || t?.symbol || '').toUpperCase();
    if (os.endsWith('CE')) return 1;
    if (os.endsWith('PE')) return -1;
    // FUTURES / CASH (God 2026-07-27): no CE/PE — direction = trade side.
    // Pehle ye 0 return karta tha → shool FUT X-Ray hamesha "0 favorable ·
    // 0 adverse" (scorer ko direction hi nahi mila).
    const side = String(data?.trade?.side || t?.side || '').toUpperCase();
    if (side === 'BUY') return 1;
    if (side === 'SELL') return -1;
    return 0;
  };
  // verdict per field: 'good' (favorable tailwind), 'bad' (adverse), 'neu' (neutral)
  type Fav = 'good' | 'bad' | 'neu';
  const XV: Record<Fav, { c: string; bg: string }> = {
    good: { c: '#34d399', bg: 'rgba(16,185,129,.12)' },
    bad:  { c: '#f87171', bg: 'rgba(239,68,68,.12)' },
    neu:  { c: '#94a3b8', bg: 'transparent' },
  };
  const favWord = (v: string, dir: number): Fav => {
    const up = /up|bull/i.test(v || ''), dn = /down|bear/i.test(v || '');
    if (!dir || (!up && !dn)) return 'neu';
    return (up && dir > 0) || (dn && dir < 0) ? 'good' : 'bad';
  };
  const favNum = (v: any, dir: number, dead = 0.02): Fav => {
    const n = Number(v);
    if (!dir || v == null || Number.isNaN(n) || Math.abs(n) < dead) return 'neu';
    return (n > 0) === (dir > 0) ? 'good' : 'bad';
  };
  const favRsi = (v: any, dir: number): Fav => {
    const n = Number(v);
    if (!dir || v == null || Number.isNaN(n)) return 'neu';
    if (dir > 0) return n >= 78 ? 'neu' : n >= 53 ? 'good' : n <= 45 ? 'bad' : 'neu';   // bullish momentum
    return n <= 22 ? 'neu' : n <= 47 ? 'good' : n >= 55 ? 'bad' : 'neu';                  // bearish momentum
  };
  const favBool = (v: any, dir: number): Fav => (!dir || !v ? 'neu' : dir > 0 ? 'good' : 'bad');

  const exportCSV = () => {
    const data = filteredTrades;
    if (data.length === 0) return showToast('❌ No data to export!');
    const headers = 'TIME,DATE,SYMBOL,SIDE,QTY,ENTRY,EXIT,STATUS,BROKER,CLIENT,BOT,NET PNL';
    const rows = data.map(t => `${t.time},${t.date},${t.symbol},${t.side},${t.qty},${t.entryPrice},${t.exitPrice},${t.status},${t.broker},${t.client},${t.botName},${t.pnl.toFixed(2)}`);
    const csv = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = `Jaiswal_${reportTab}_Report_${dateFilter}.csv`;
    document.body.appendChild(link); link.click(); link.remove();
    showToast('✅ CSV Exported!');
  };

  // PRINT / PDF — mirrors the in-app hierarchical report exactly (Bot → Symbol →
  // TF badge → Client), each section sorted by Net P&L. Same buildHierReport()
  // source as the modal, so the printed page == what's on screen. God 2026-07-23.
  const handlePrint = () => {
    const data = filteredTrades;
    if (data.length === 0) return showToast('❌ No data to print!');
    const w = window.open('', '_blank');
    if (!w) return;
    const esc = (s: any) => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' } as Record<string, string>)[c]);
    const money = (n: number) => `${n < 0 ? '−' : '+'}₹${Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const pill = (s: string) => {
      const bg = s === 'WIN' ? '#16a34a' : s === 'BREAKEVEN' ? '#eab308' : '#dc2626';
      return `<span style="background:${bg};color:#fff;padding:2px 9px;border-radius:5px;font-weight:800;font-size:10px">${s === 'BREAKEVEN' ? 'BE' : s}</span>`;
    };
    const grand = data.reduce((a, t) => a + t.pnl, 0);
    const sections = hierReport.map((bot, bi) => {
      const banner = `<div class="botband" style="border-left:6px solid ${bot.col}">
        <span class="botttl">${bi + 1}. ${esc(bot.name)}${bot.tag ? ` <span class="bottag">(${esc(bot.tag)})</span>` : ''}</span>
        <span class="botpnl" style="color:${bot.net >= 0 ? '#16a34a' : '#dc2626'}">${(() => { const u = usageOfCells(bot.symbols.map((x: any) => ({ trades: x.trades, net: x.net })), bot.net); return `<span class="fu">Fund ${inrShort(u.fund)} · Win ${u.win === null ? '—' : u.win.toFixed(1) + '%'} · Return ${pctTxt(u.ret)}</span> `; })()}Net P&amp;L: ${money(bot.net)}</span>
      </div>`;
      if (bot.symbols.length === 0) return banner + `<div class="empty">No trades executed today. ${idleNote(bot.model)}</div>`;
      const syms = bot.symbols.map(sym => {
        const tfBadges = sym.tfs.map(tf => `<span class="tf">⏱ ${esc(tf)}</span>`).join(' ');
        const rows = sym.trades.map(t => { const sp = splitSymbol(t.symbol); return `<tr>
          <td>${esc(t.date)}</td><td>${esc(fmtTime(t.entryTime || t.time))}</td><td>${esc(fmtTime(t.exitTime))}</td>
          <td class="strike">${esc(sp.strike || sp.base)}</td>
          <td style="color:${t.side === 'BUY' ? '#16a34a' : '#dc2626'};font-weight:700">${esc(t.side)}</td>
          <td style="text-align:right">${esc(t.qty)}</td><td>${esc(t.timeframe || '—')}</td><td>${esc(t.client || 'All Clients')}</td>
          <td>${pill(t.status)}</td>
          <td style="text-align:right;font-weight:800;color:${t.pnl >= 0 ? '#16a34a' : '#dc2626'}">${money(t.pnl)}</td>
        </tr>`; }).join('');
        // Counts printed alongside the P&L so a printed section can be checked
        // on its own — same figures the on-screen header carries (God 2026-08-24).
        return `<div class="symhdr">
            <span class="symname">${esc(sym.symbol)} ${tfBadges}</span>
            <span class="sympnl" style="color:${sym.net >= 0 ? '#16a34a' : '#dc2626'}">${sym.trades.length} · ${sym.wins}W/${sym.losses}L/${sym.be}BE &nbsp; ${(() => { const u = usageOf(sym.trades, sym.net); return `<span class="fu">Fund ${inrShort(u.fund)} · Win ${u.win === null ? '—' : u.win.toFixed(1) + '%'} · ${pctTxt(u.ret)}</span> `; })()}P&amp;L: ${money(sym.net)}</span>
          </div>
          <table class="tt"><thead><tr>
            <th>ENTRY DATE</th><th>ENTRY TIME</th><th>EXIT TIME</th><th>STRIKE</th><th>SIDE</th><th>QTY</th><th>TF</th><th>CLIENT</th><th>STATUS</th><th>NET P&amp;L</th>
          </tr></thead><tbody>${rows}</tbody></table>`;
      }).join('');
      return banner + syms;
    }).join('');
    const winRate = data.length > 0 ? ((data.filter(t => t.status === 'WIN').length / data.length) * 100).toFixed(1) : '0';
    w.document.write(`<html><head><title>Jaiswal Capital — ${esc(reportTab)} Report ${esc(dateFilter)}</title>
      <style>
        *{box-sizing:border-box}
        body{font-family:'Segoe UI',Arial,sans-serif;padding:26px;color:#1e293b;background:#fff}
        h1{font-size:20px;margin:0 0 4px;color:#0f172a}
        .meta{color:#64748b;font-size:12px;margin-bottom:14px}
        .grand{display:inline-block;margin:0 0 8px;padding:10px 16px;border-radius:8px;background:#0f172a;color:#fff;font-weight:800;font-size:14px}
        .botband{display:flex;align-items:center;justify-content:space-between;background:#334155;color:#fff;padding:12px 16px;border-radius:6px;margin:22px 0 12px}
        .botttl{font-size:16px;font-weight:800}.bottag{font-weight:500;opacity:.82;font-size:13px}
        .botpnl{font-size:15px;font-weight:800;background:#fff;padding:4px 10px;border-radius:5px}
        .symhdr{display:flex;align-items:center;justify-content:space-between;background:#eef2f7;padding:10px 14px;border-radius:5px;margin:14px 0 0}
        .symname{font-size:14px;font-weight:800;color:#2563eb}
        .tf{font-size:9px;font-weight:700;color:#4338ca;background:#e0e7ff;padding:2px 7px;border-radius:10px;margin-left:4px}
        .sympnl{font-size:14px;font-weight:800}
        .fu{font-size:11px;font-weight:700;color:#4338ca;margin-right:8px}
        table.tt{width:100%;border-collapse:collapse;margin:0 0 6px}
        table.tt th{background:#334155;color:#fff;padding:7px 8px;font-size:10px;text-align:left;letter-spacing:.3px}
        table.tt td{border:1px solid #e2e8f0;padding:6px 8px;font-size:11px}
        .strike{font-weight:700}
        .empty{padding:16px;text-align:center;font-style:italic;color:#94a3b8;border:1px dashed #cbd5e1;border-radius:6px;margin-bottom:8px}
        @media print{body{padding:12px}.botband,table.tt th,.grand{-webkit-print-color-adjust:exact;print-color-adjust:exact}.botband,.symhdr,table.tt{break-inside:avoid}}
      </style></head><body>
      <h1>🏦 JAISWAL CAPITAL — ${esc(reportTab)} TRADING REPORT</h1>
      <div class="meta">Filter: ${esc(dateFilter)} · Bot: ${esc(botLabel(botFilter))} · Trades: ${data.length} · Win Rate: ${winRate}%</div>
      <div class="grand">GRAND TOTAL NET P&amp;L: <span style="color:${grand >= 0 ? '#4ade80' : '#f87171'}">${money(grand)}</span></div>
      ${sections}
      </body></html>`);
    w.document.close(); setTimeout(() => w.print(), 450);
  };

  const toggleClient = (cid: string) => {
    setNewDep(p => {
      const cur = p.targetClients || [];
      if (cid === 'ALL') return { ...p, targetClients: ['ALL'] };
      const f = cur.filter(c => c !== 'ALL');
      if (f.includes(cid)) {
        const nxt = f.filter(c => c !== cid);
        return { ...p, targetClients: nxt.length ? nxt : ['ALL'] };
      }
      return { ...p, targetClients: [...f, cid] };
    });
  };

  // 🎛 Per-client routing patch (multi-client × multi-broker fire matrix)
  const setClientRoute = (cid: string, patch: Partial<{ enabled: boolean; broker: string; qtyMult: number }>) =>
    setNewDep(p => ({ ...p, clientRouting: { ...(p.clientRouting || {}), [cid]: { ...((p.clientRouting || {})[cid] || {}), ...patch } } }));

  const toggleBroker = (bid: string) => {
    setNewDep(p => {
      const cur = p.targetBrokers || [];
      if (cur.includes(bid)) {
        // GUARD: empty targetBrokers = orders route KAHIN nahi — last broker
        // remove nahi hota (pehle silently sab deselect ho sakta tha).
        if (cur.length <= 1) { showToast('❌ Kam se kam ek broker chahiye — routing empty nahi ho sakti'); return p; }
        return { ...p, targetBrokers: cur.filter(b => b !== bid) };
      }
      return { ...p, targetBrokers: [...cur, bid] };
    });
  };

  const symbolSuggestions = useMemo(() => {
    const t = tempAsset.type;
    if (t === 'OPTION' || t === 'FUTURE') return [...INDICES_LIST, ...NIFTY_50_FNO_LIST];
    if (t === 'STOCK')     return NIFTY_50_FNO_LIST;
    if (t === 'COMMODITY') return COMMODITIES_LIST;
    if (t === 'ETF')       return ETFS;
    if (t === 'INDEX')     return INDICES_LIST;
    return [];
  }, [tempAsset.type]);

  const baseUpperSym = (tempAsset.baseSymbol || tempAsset.symbol.split(' ')[0]).toUpperCase();
  const baseSpot = livePrices[baseUpperSym] || 0;
  return (
    <div className="lt-root" style={{height:'100%',width:'100%',display:'flex',flexDirection:'column',background:'var(--bg,#080b12)',color:'var(--text,#e2e8f0)',fontFamily:"'Segoe UI',system-ui,sans-serif",overflow:'hidden'}}>

      <LiveConfirmModal
      confirmLiveSwitch={confirmLiveSwitch}
      liveConfirmFor={liveConfirmFor}
      liveConfirmText={liveConfirmText}
      newDep={newDep}
      setLiveConfirmText={setLiveConfirmText}
      setShowLiveConfirm={setShowLiveConfirm}
      showLiveConfirm={showLiveConfirm}
      />
      {/* Socket-degraded banner — surfaces the silent fallback to REST polling
          so the user knows live signals may be delayed. Previously the badge
          flip from LIVE → polling was only visible in the log console. */}
      {tradingMode === 'LIVE' && !socketConnected && (
        <div style={{
          background: 'rgba(251,191,36,0.12)', borderBottom: '1px solid #fbbf24',
          color: '#fbbf24', padding: '6px 14px', fontSize: 11, fontWeight: 700,
          textAlign: 'center', letterSpacing: 0.5,
        }}>
          ⚠️ WebSocket DROPPED — reconnecting with backoff. Kill-switch & expiry events may be delayed until link restores.
        </div>
      )}

      <BotActionSheet
      botMenuId={botMenuId}
      deployments={laneDeployments}
      fireDirectOrder={fireDirectOrder}
      getLotSize={getLotSize}
      openBotDepth={openBotDepth}
      setBotMenuId={setBotMenuId}
      setPricerSymbol={setPricerSymbol}
      setSelectedBotForWebhook={setSelectedBotForWebhook}
      setShowOptionPricer={setShowOptionPricer}
      setShowWebhookModal={setShowWebhookModal}
      testWebhook={testWebhook}
      setConfirmDelete={setConfirmDelete}
      />
      {toast && (
        <div className="jc-float" style={{position:'fixed',bottom:24,right:24,zIndex:9999,
          background:toast.includes('🚨')||toast.includes('❌')?'linear-gradient(135deg,#dc2626,#b91c1c)':
                     toast.startsWith('✅')?'linear-gradient(135deg,#059669,#047857)':
                     toast.startsWith('🗑️')?'linear-gradient(135deg,#991b1b,#7f1d1d)':
                     'linear-gradient(135deg,#2563eb,#1d4ed8)',
          color:'#fff',padding:'12px 20px',borderRadius:10,fontSize:12,fontWeight:700,boxShadow:'0 8px 32px rgba(0,0,0,.5)'}}>
          {toast}
        </div>
      )}

      {/* KALI SECURITY STRIP - collapsed by default so it never displaces the
          trading UI, but always present so the armed/disarmed state is one glance
          away on the page that can spend real money. */}
      {IS_KALI && <KaliSecurityPanel />}

      <PageHeader
      IS_KALI={IS_KALI}
      backendConnected={backendConnected}
      currentExpiry={currentExpiry}
      deployments={laneDeployments}
      enableNotifications={enableNotifications}
      expiryRolled={expiryRolled}
      fleetClients={fleetClients}
      handleModeSwitch={handleModeSwitch}
      killSwitchActive={killSwitchActive}
      liveSignals={laneSignals}
      mamtaBots={mamtaBots}
      masterSwitch={masterSwitch}
      setEditingBotId={setEditingBotId}
      setEnableNotifications={setEnableNotifications}
      setNameAuto={setNameAuto}
      setPricerSymbol={setPricerSymbol}
      setShowDepthPanel={setShowDepthPanel}
      setShowKillPanel={setShowKillPanel}
      setShowModal={setShowModal}
      setShowOptionPricer={setShowOptionPricer}
      setShowSignalStream={setShowSignalStream}
      setSignalStreamMinimized={setSignalStreamMinimized}
      setThemeMode={setThemeMode}
      showDepthPanel={showDepthPanel}
      showSignalStream={showSignalStream}
      signalStreamMinimized={signalStreamMinimized}
      socketConnected={socketConnected}
      themeMode={themeMode}
      toggleMaster={toggleMaster}
      tradingMode={tradingMode}
      />
      {/* SYMBOL CONTROL (L0) strip RELOCATED into the DEPLOYED STRATEGIES panel
          header to declutter the top hero (God/Subhas 2026-07-22). Position
          Manager already lives in the ⚡ POSITIONS tab (God 2026-07-15). */}

      {/* PINE HISTORY (God 2026-10-12): FAIL / replaced Pines closed with their own win rate */}
      <PineHistoryPanel mode={tradingMode} />
      {/* HEADER STATS — now ./liveTrading/HeaderStatsBar */}
      <HeaderStatsBar
        onOpenPnlDrawer={() => setPnlDrawerOpen(true)}
        onSkippedClick={() => setSkippedLedgerOpen(true)}
        sessionLabelText={sessionLabel()}
        lastSyncAt={lastSyncAt}
        backendConnected={backendConnected}
        backendOpenTrades={laneOpenTrades}
        backendSummary={backendSummary}
        liveSignals={laneSignals}
        runningDeps={runningDeps}
        sigCounts={sigCounts}
        isMobile={isMobile}
        tradingMode={tradingMode}
        todayPnl={todayPnl}
        totalPnl={totalPnl}
        totalTrades={totalTrades}
        tradesToday={tradesToday}
        winRate={winRate}
        setReportTab={setReportTab}
        setShowHistoryModal={setShowHistoryModal}
        runningM2M={openM2M.m2m}
        openPriced={openM2M.priced}
        openCount={openM2M.open}
      />

      {/* ── MOBILE EXECUTION (blueprint Phase 7, God 2026-09-04) ────────────
          Phone only. It does NOT place orders: it calls fireDirectOrder, which
          emits a SIGNAL into the same router the TradingView webhooks use, so
          every gate the desktop honours applies unchanged. `src:'mobile'` is
          passed EXPLICITLY so a mobile-originated order stays separable in the
          ledger forever — never inferred from the viewport, because a desktop
          browser dragged narrow is still a desktop.

          The bot and symbol are the SAME fallback fireDirectOrder already uses
          (first RUNNING deployment, its first asset) and are printed on the bar,
          so what a hold would fire is never ambiguous. */}
      {isPhone && (() => {
        const execBot = runningDeps[0];
        const execAsset = execBot?.assets?.[0];
        const execSym = execAsset?.baseSymbol || execAsset?.symbol || '';
        const execQty = execAsset?.qty || 0;
        const ready = !!execBot && !!execSym;
        return (
          <div style={{ padding: '8px 8px 0' }}>
            <ExecutionArmBar
              lane={tradingMode}
              disabled={!ready}
              disabledReason={!execBot
                ? 'No RUNNING bot — orders only ever leave through a bot, so there is nothing to route a signal to.'
                : !execSym ? 'The running bot has no asset configured.' : undefined}
              contextLabel={ready ? `${execBot.name} · ${execSym}${execAsset?.optType ? ' ' + execAsset.optType : ''} × ${execQty}` : undefined}
              onFire={(action) => fireDirectOrder(
                execSym, action, execQty, tradingMode,
                execAsset?.optType, undefined, 'NRML', execBot?.id, 'mobile',
              )}
            />
          </div>
        );
      })()}

      {/* MAIN CONTENT */}
      <div style={{flex:1,display:'grid',gridTemplateColumns:isMobile?'1fr':'minmax(0,1fr) 360px',gap:12,padding:isMobile?8:12,overflow:isMobile?'auto':'hidden'}}>
        <div style={{background:'#0d1117',borderRadius:10,border:'1px solid #1e293b',display:'flex',flexDirection:'column',overflow:'hidden'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #1e293b',fontSize:11,fontWeight:800,color:'#94a3b8',textTransform:'uppercase',display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <span>Deployed Strategies ({deployments.length})</span>
            {/* On a phone these five chips plus the RUNNING badge wrap to three
                lines and push the strategy list below the fold before it has
                rendered a single row. One horizontal scroller instead — the
                standard phone pattern, and it keeps the header one line tall.
                `WebkitOverflowScrolling` keeps the momentum flick on iOS. */}
            <div style={{display:'flex',gap:6,alignItems:'center',
              ...(isPhone ? { width:'100%', overflowX:'auto' as const, flexWrap:'nowrap' as const,
                              WebkitOverflowScrolling:'touch' as const, paddingBottom:2,
                              scrollbarWidth:'none' as const } : {})}}>
              {/* 🆕 v7.0 — Source filter chips */}
              {[
                {id:'all',label:'ALL',col:'#94a3b8'},
                {id:'mamta_ai',label:'🔱 MAMTA',col:'#a855f7'},
                {id:'manual',label:'MANUAL',col:'#fbbf24'},
                {id:'webhook',label:'📡 TV',col:'#10b981'},
                {id:'positions',label:'⚡ POSITIONS',col:'#22d3ee'},
                {id:'traffic',label:'🚦 TRAFFIC',col:'#f59e0b'},
                {id:'promotion',label:'🚀 PAPER→LIVE',col:'#ef4444'},
              ].map(f => (
                <button key={f.id} onClick={()=>setShowOnlySource(f.id as any)}
                  className="tab-btn"   /* 📱 ≤480px: existing .tab-btn rule = 44px touch target */
                  style={{padding:'3px 8px',borderRadius:5,fontSize:8,fontWeight:800,letterSpacing:1,
                    background:showOnlySource===f.id?`${f.col}22`:'transparent',
                    color:showOnlySource===f.id?f.col:'#64748b',
                    border:`1px solid ${showOnlySource===f.id?f.col+'55':'#1e293b'}`,
                    cursor:'pointer'}}>
                  {f.label}
                </button>
              ))}
              <span style={{color:'#10b981',marginLeft:6}}>● {runningDeps.length} RUNNING</span>
            </div>
          </div>
          <div className="lt-scroll" style={{flex:1,overflowY:'auto',padding:8}}>
            {/* SYMBOL CONTROL (L0) — INSIDE the tab content, collapsed by default so
                it takes ZERO permanent vertical space (God/Subhas 2026-07-22). It
                must NOT sit exposed above the filter tabs. Surgical per-symbol kill
                blocks NEW entries across all bots; exits are never blocked. */}
            {/* CAS HOLDING GATES moved to GOD APPROVAL / PIPELINE in RETIRED mode
                (God 2026-10-09): its ranking was frozen at 07-Sep and no consumer
                read the decisions, so it showed stale data with dead buttons. */}
            <SymbolControlAccordion/>
            {/* ⚡ POSITIONS tab (God 2026-07-15): DEPLOYED STRATEGIES panel ke
                andar hi position manager ka full detail — FULL SCREEN bhi yahi se.
                🛡️ MASTER FLEET REGISTRY (God 2026-07-18, moved here 13:55 order):
                POSITIONS ke andar, full position-system ke UPAR — teeno Rudra bots
                ki permanent binding, server-SSOT auto-sync (zero manual entry),
                per-bot × symbol × TF PnL. Display-only: status flips God-gated. */}
            {/* SELF-FETCHING PANELS (God 2026-08-26). These two call the API
                themselves instead of taking a lane-filtered array, which is
                exactly how the KALI page went on showing all seven PAPER bots
                with RUNNING/PAPER badges after the lane fix — filtering upstream
                could never reach them. They must be told the lane explicitly. */}
            {/* SIGNAL TRAFFIC (God 2026-09-30): own tab beside POSITIONS - candle -> brain -> order clocks per symbol x TF, 30 ms budget. */}
            {showOnlySource === 'traffic' && <TrafficGrid />}
            {/* PAPER -> LIVE PROMOTION (God 2026-09-30): ladder on the KALI D6 gateway, beside TRAFFIC. */}
            {showOnlySource === 'promotion' && <PromotionLadder />}
            {showOnlySource === 'positions' && <FleetRegistryCards lane={lane} />}
            {showOnlySource === 'positions' && <GranularPositionManager variant="panel" lane={lane} />}
            {/* WHAT "EMPTY" MEANS CHANGED (God 2026-08-28). On KALI this used to
                fire constantly and correctly: the lane dropped every non-LIVE bot
                and the fleet is 100% paper. KALI now lists the WHOLE fleet with a
                PENDING badge, so an empty KALI page means no deployment loaded at
                all — a fault, not a fact. Worth saying out loud, because the old
                copy would have reassured the operator about a real outage. */}
            {laneDeployments.length === 0 && (
              <div style={{
                margin: '10px 0', padding: '14px 16px', borderRadius: 8,
                background: IS_KALI ? 'rgba(239,68,68,.07)' : 'rgba(56,189,248,.07)',
                border: `1px solid ${IS_KALI ? 'rgba(239,68,68,.35)' : 'rgba(56,189,248,.3)'}`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 900, color: IS_KALI ? '#ef4444' : '#38bdf8' }}>
                  {IS_KALI ? '🔱 KALI — no bots loaded at all' : '🧪 DUMABATI — no PAPER bots deployed'}
                </div>
                <div style={{ fontSize: 9.5, color: 'var(--text-muted,#94a3b8)', marginTop: 4, lineHeight: 1.5 }}>
                  {IS_KALI
                    ? <>KALI carries the <b>same complete fleet</b> as DUMABATI; a bot that is not yet
                        live-approved stays listed as <b>⏳ WAITING FOR PINE V6 APPROVAL</b> with its
                        execution blocked. So an empty page here is <b>not</b> "no LIVE bots" — it means the
                        deployments fetch returned nothing. Check the API before assuming this is normal.</>
                    : <>This lane shows <b>only PAPER</b> bots. The fleet currently holds{' '}
                        {deployments.filter(d => String(d.tradingMode || 'PAPER').toUpperCase() === 'LIVE').length} LIVE
                        {' '}and {deployments.filter(d => String(d.tradingMode || 'PAPER').toUpperCase() !== 'LIVE').length} PAPER
                        {' '}bot(s) — the others live on <b>LIVE AUTO TRADING KALI</b>. Nothing is hidden or lost.</>}
                </div>
              </div>
            )}
            {/* DEPLOYED STRATEGY CARDS — now ./liveTrading/StrategyCardList */}
            <StrategyCardList
              deployments={laneDeployments}
              botStats={botStats}
              botStatsToday={botStatsToday}
              botStatsLoaded={botStatsLoaded}
              botDepth={botDepth}
              fleetClients={fleetClients}
              showOnlySource={showOnlySource}
              isMobile={isMobile}
              onOpenChart={onOpenChart}
              getLotSize={getLotSize}
              getProductLabel={getProductLabel}
              hydrateEditAssets={hydrateEditAssets}
              openBotDepth={openBotDepth}
              testWebhook={testWebhook}
              toggleDep={toggleDep}
              fireDirectOrder={fireDirectOrder}
              setBotMenuId={setBotMenuId}
              setConfirmDelete={setConfirmDelete}
              setEditingBotId={setEditingBotId}
              setSelectedBotForWebhook={setSelectedBotForWebhook}
              setNameAuto={setNameAuto}
              setNewDep={setNewDep}
              setPricerSymbol={setPricerSymbol}
              setShowModal={setShowModal}
              setShowOptionPricer={setShowOptionPricer}
              setShowWebhookModal={setShowWebhookModal}
            />
          </div>
        </div>

        {/* Right: OPEN POSITIONS (top — market me sabse pehle yahi chahiye) +
            Console neeche (God layout R&D 2026-07-15) */}
        <div style={{background:'#0d1117',borderRadius:10,border:'1px solid #1e293b',display:'flex',flexDirection:'column',overflow:'hidden'}}>
          {/* OPEN POSITIONS — now ./liveTrading/OpenPositionsPanel */}
          <OpenPositionsPanel
            backendConnected={backendConnected}
            backendOpenTrades={laneOpenTrades}
            openPnl={openPnl}
            setShowOnlySource={setShowOnlySource}
            liveLtps={liveLtps}
            bookedPnl={todayPnl}
            bookedLabel={sessionLabel()}
            onOpenChart={(row: any) => {
              // God 2026-10-14: an option position opens the index (flow) AND its option chart (decision) together
              const p = contractParts(row);
              setChartTarget(p.kind === 'OPTION'
                ? chartTargetForPinpoint(String(row.botId || ''), p.underlyingKey, String(row.timeframe || ''), row)
                : chartTargetForRow(row));
            }}
          />
          {/* EXECUTION CONSOLE — now ./liveTrading/ExecutionConsolePanel */}
          <ExecutionConsolePanel
            logs={logs}
            logFilter={logFilter}
            setLogFilter={setLogFilter}
            showPings={showPings}
            setShowPings={setShowPings}
            fetchBackendData={fetchBackendData}
            consoleRef={consoleRef}
          />
        </div>
      </div>
      {/* DELETE MODAL */}
      {confirmDelete && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.85)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div style={{background:'linear-gradient(135deg,#1e293b,#0f172a)',borderRadius:12,
            padding:'min(24px, 5vw)',width:'min(440px, calc(100vw - 24px))',border:'1px solid #dc2626'}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
              <AlertTriangle size={24} color="#f87171"/>
              <div style={{fontSize:14,fontWeight:800,color:'#fff'}}>Delete Bot Permanently?</div>
            </div>
            <div style={{fontSize:11,color:'#94a3b8',lineHeight:1.6,marginBottom:16}}>
              You are about to delete: <br/>
              <strong style={{color:'#fbbf24'}}>{deployments.find(d => d.id === confirmDelete)?.name}</strong>
              <br/><br/>
              This action <strong style={{color:'#f87171'}}>cannot be undone</strong>.
            </div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>setConfirmDelete(null)} className="lt-btn" style={{flex:1,padding:10,background:'#334155',color:'#fff',fontSize:10}}>CANCEL</button>
              <button onClick={()=>deleteBot(confirmDelete)} className="lt-btn" style={{flex:1,padding:10,background:'linear-gradient(135deg,#dc2626,#b91c1c)',color:'#fff',fontSize:10}}>
                <Trash2 size={12}/> DELETE PERMANENTLY
              </button>
            </div>
          </div>
        </div>
      )}

      <WebhookModal
      deployments={laneDeployments}
      selectedBotForWebhook={selectedBotForWebhook}
      setDeployments={setDeployments}
      setShowWebhookModal={setShowWebhookModal}
      showToast={showToast}
      showWebhookModal={showWebhookModal}
      testWebhook={testWebhook}
      tvToken={tvToken}
      />
      {pnlDrawerOpen && (
        <Suspense fallback={null}>
          <PnlDeepDrawer lane={tradingMode} onClose={() => setPnlDrawerOpen(false)} />
        </Suspense>
      )}
      {skippedLedgerOpen && (
        <Suspense fallback={null}>
          <SkippedCensusLedger lane={lane} date={sessionIso()} isMobile={isMobile} onClose={() => setSkippedLedgerOpen(false)} />
        </Suspense>
      )}
      {/* CHART quick action (God Phase 3, 2026-09-24): the open row's own
          contract at its own timeframe, in a view-only SuperChart pane. */}
      {chartTarget && (
        <Suspense fallback={null}>
          <ContractChartDrawer target={chartTarget} lane={lane} onClose={() => setChartTarget(null)} />
        </Suspense>
      )}
      {showHistoryModal && (<React.Suspense fallback={null}><TradeHistoryModal
      backendOpenTrades={laneOpenTrades}
      botFilter={botFilter}
      botLabel={botLabel}
      collapsedGroups={collapsedGroups}
      dateFilter={dateFilter}
      exportCSV={exportCSV}
      filteredTrades={filteredTrades}
      groupBy={groupBy}
      handlePrint={handlePrint}
      hierReport={hierReport}
      idleNote={idleNote}
      liveTrades={liveTrades}
      openXray={openXray}
      paperTrades={paperTrades}
      reportGrand={reportGrand}
      reportGroups={reportGroups}
      reportTab={reportTab}
      setBotFilter={setBotFilter}
      setDateFilter={setDateFilter}
      setGroupBy={setGroupBy}
      setReportTab={setReportTab}
      setShowHistoryModal={setShowHistoryModal}
      setSymbolFilter={setSymbolFilter}
      setTfFilter={setTfFilter}
      showHistoryModal={showHistoryModal}
      splitSymbol={splitSymbol}
      sqOffAllBusy={sqOffAllBusy}
      sqOffBusy={sqOffBusy}
      squareOffAllPositions={squareOffAllPositions}
      squareOffPosition={squareOffPosition}
      liveLane={IS_KALI || tradingMode === 'LIVE'}
      symbolFilter={symbolFilter}
      tfFilter={tfFilter}
      toggleGroup={toggleGroup}
      uniqueBots={uniqueBots}
      uniqueDates={uniqueDates}
      uniqueSymbols={uniqueSymbols}
      uniqueTFs={uniqueTFs}
      /></React.Suspense>)}
      {xray && (<React.Suspense fallback={null}><SmcXrayModal
      XV={XV}
      favBool={favBool}
      favNum={favNum}
      favRsi={favRsi}
      favWord={favWord}
      setXray={setXray}
      xray={xray}
      xrayDir={xrayDir}
      /></React.Suspense>)}
      {/* 📱 STICKY MOBILE ACTION BAR — KILL ALL + DEPLOY thumb-reach par
          (God mobile-first mandate). Desktop par header buttons hi kaafi hain. */}
      {!showModal && typeof document !== 'undefined' && createPortal(
        // NEVER VISIBLE BEFORE (measured 2026-09-24, iPhone viewport): an ancestor's
        // transform/filter turned position:fixed into "fixed to that ancestor", so
        // the bar sat at y=2638 on a 664-px screen - below the page end, under the
        // app nav. Portalled to <body> it is truly fixed, and rides ON the app nav
        // (App writes the nav's height to --jc-mnav-h).
        <div className="mob-flex" style={{position:'fixed',left:0,right:0,bottom:'var(--jc-mnav-h, calc(64px + env(safe-area-inset-bottom)))',zIndex:900,
          gap:10,padding:'6px 12px',
          background:'rgba(10,12,16,0.9)',backdropFilter:'blur(16px)',WebkitBackdropFilter:'blur(16px)',
          borderTop:'1px solid rgba(148,163,184,0.14)'}}>
          <button onClick={()=>setShowKillPanel(true)} className={`lt-btn ripple ${killSwitchActive?'glow-kill':''}`}
            style={{flex:1,minHeight:48,background:killSwitchActive?'linear-gradient(135deg,#dc2626,#b91c1c)':'rgba(239,68,68,.12)',
              color:killSwitchActive?'#fff':'#ef4444',border:'1px solid rgba(239,68,68,.4)',fontSize:11,fontWeight:900,letterSpacing:1}}>
            🛑 {killSwitchActive?'KILLED':'KILL ALL'}
          </button>
          <button onClick={()=>{setEditingBotId(null); setNameAuto(true); setShowModal(true);}} className="lt-btn ripple"
            style={{flex:1,minHeight:48,background:'linear-gradient(135deg,#4f46e5,#6366f1)',color:'#fff',fontSize:11,fontWeight:900,letterSpacing:1}}>
            🚀 DEPLOY BOT
          </button>
        </div>,
        document.body,
      )}

      {showModal && (<React.Suspense fallback={null}><DeployModal
      addAssetToBasket={addAssetToBasket}
      assetClientPanel={assetClientPanel}
      baseSpot={baseSpot}
      baseUpperSym={baseUpperSym}
      calcAutoExpiry={calcAutoExpiry}
      calcAutoStrike={calcAutoStrike}
      clientSearch={clientSearch}
      clientSyncBusy={clientSyncBusy}
      deployBot={deployBot}
      editingBotId={editingBotId}
      fleetClients={fleetClients}
      getLotSize={getLotSize}
      isMobile={isMobile}
      modelQuickKinds={modelQuickKinds}
      modelTypeLock={modelTypeLock}
      newDep={newDep}
      passedSyms={passedSyms}
      quickAddAsset={quickAddAsset}
      quickAddLists={quickAddLists}
      refreshFleetClients={refreshFleetClients}
      removeAssetFromBasket={removeAssetFromBasket}
      routeAcc={routeAcc}
      rudraPineId={rudraPineId}
      setAssetClientPanel={setAssetClientPanel}
      setClientRoute={setClientRoute}
      setClientSearch={setClientSearch}
      setEditingBotId={setEditingBotId}
      setLiveConfirmFor={setLiveConfirmFor}
      setLiveConfirmText={setLiveConfirmText}
      setNameAuto={setNameAuto}
      setNewDep={setNewDep}
      setPricerSymbol={setPricerSymbol}
      setShowLiveConfirm={setShowLiveConfirm}
      setShowModal={setShowModal}
      setShowOptionPricer={setShowOptionPricer}
      setTempAsset={setTempAsset}
      showModal={showModal}
      showToast={showToast}
      symbolSuggestions={symbolSuggestions}
      tempAsset={tempAsset}
      toggleBroker={toggleBroker}
      toggleClient={toggleClient}
      updateAssetLots={updateAssetLots}
      updateAssetSymbol={updateAssetSymbol}
      updateBasketLots={updateBasketLots}
      /></React.Suspense>)}
      {/* DELIBERATELY NOT LANE-SCOPED (God 2026-08-26). Every other panel on
          this page now sees only its own lane, and this one must not: the button
          says GLOBAL KILL SWITCH, and a control that claims to stop everything
          while quietly stopping half of it is the same class of lie as a depth
          panel claiming 20 levels over a 5-level book. Safety points the same
          way — stopping must always be easier than starting, so a kill fired
          from either page reaches every bot in both lanes. */}
      <KillSwitchPanel
      addLog={addLog}
      backendOpenTrades={backendOpenTrades}
      backendSummary={backendSummary}
      deployments={deployments}
      killSwitchActive={killSwitchActive}
      setDeployments={setDeployments}
      setKillSwitchActive={setKillSwitchActive}
      setMasterSwitch={setMasterSwitch}
      setShowKillPanel={setShowKillPanel}
      showKillPanel={showKillPanel}
      showToast={showToast}
      socketRef={socketRef}
      />
      {/* 🆕 v6.0 — SMART OPTION PRICER PANEL */}
      <SmartOptionPricerPanel
        symbol={pricerSymbol}
        spot={livePrices[pricerSymbol] || 0}
        expiry={currentExpiry}
        open={showOptionPricer}
        onClose={()=>setShowOptionPricer(false)}
        onPickStrike={handlePricerPick}
        onSymbolChange={s => setPricerSymbol(s)}
      />

      {/* ⭐ v11.0 — MARKET DEPTH PANEL */}
      <MarketDepthPanel
        symbol={depthSymbol}
        open={showDepthPanel}
        onClose={()=>setShowDepthPanel(false)}
        focus={depthFocus}
        onSymbolChange={s=>setDepthSymbol(s.toUpperCase())}
        kali={IS_KALI}
      />

      {/* 🆕 v6.0/v7.0 — LIVE SIGNAL STREAM PANEL (with minimize support) */}
      {showSignalStream && (
        <LiveSignalStreamPanel
          signals={laneSignals}
          onClose={()=>{setShowSignalStream(false); setSignalStreamMinimized(false);}}
          minimized={signalStreamMinimized}
          onToggleMinimize={()=>setSignalStreamMinimized(v=>!v)}
          onDeploySignal={deployFromSignal}
          onOpenChart={onOpenChart}
        />
      )}
    </div>
  );
};

